import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronDown,
  ChevronRight,
  Target,
  MessageSquare,
  CheckCircle2,
  DollarSign,
  Users,
  Building2,
  Phone,
  Mail,
  Calendar,
  Zap,
  Shield,
  TrendingUp,
  Clock,
  AlertTriangle,
  Truck,
  Wrench,
  Package,
  ArrowLeft,
  Clipboard,
  FileText,
  Star,
  Quote,
  Play,
  ExternalLink,
  Droplets,
  FlaskConical,
  Activity,
  Award,
  MapPin,
  Settings,
  Gauge,
  Microscope,
  BadgeCheck,
  Factory,
  CircleCheck,
  Layers,
  BarChart3,
  Download,
  Lock,
  Smartphone,
  Database,
  Sparkles,
  Thermometer,
  Server
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PDFDownloadLink } from "@react-pdf/renderer";
import ProductDatasheetPDF from "@/components/ProductDatasheetPDF";
import GeneralContractorBrochurePDF from "@/components/GeneralContractorBrochurePDF";

const ASKISAAC_LOGO = "/logos/askisaac-b-monogram.svg";
const GEORGE_IMAGE = "https://customer-assets.emergentagent.com/job_service-widgets/artifacts/n8vnfz06_Gemini_Generated_Image_ntzefxntzefxntze%20%281%29.png";

// Expandable Section Component
const Section = ({ title, icon: Icon, children, defaultOpen = false, color = "blue" }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  const colorClasses = {
    blue: "from-blue-500 to-cyan-500 text-blue-600",
    green: "from-emerald-500 to-teal-500 text-emerald-600",
    purple: "from-violet-500 to-purple-500 text-violet-600",
    orange: "from-orange-500 to-amber-500 text-orange-600",
    red: "from-red-500 to-rose-500 text-red-600"
  };
  
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden mb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-white hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${colorClasses[color].split(' ')[0]} ${colorClasses[color].split(' ')[1]} flex items-center justify-center`}>
            <Icon className="w-5 h-5 text-white" />
          </div>
          <span className="font-semibold text-slate-800 text-lg">{title}</span>
        </div>
        {isOpen ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
      </button>
      {isOpen && (
        <div className="p-6 bg-slate-50 border-t border-slate-200">
          {children}
        </div>
      )}
    </div>
  );
};

// Script Card Component
const ScriptCard = ({ title, script, tips }) => (
  <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
    <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
      <MessageSquare className="w-4 h-4 text-blue-500" />
      {title}
    </h4>
    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg mb-3">
      <p className="text-slate-700 italic">&quot;{script}&quot;</p>
    </div>
    {tips && (
      <div className="text-sm text-slate-600">
        <span className="font-medium">💡 Tip:</span> {tips}
      </div>
    )}
  </div>
);

// Objection Handler Component
const ObjectionHandler = ({ objection, response }) => (
  <div className="bg-white rounded-lg border border-slate-200 p-4 mb-3">
    <div className="flex items-start gap-3 mb-3">
      <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
      <div>
        <span className="font-medium text-slate-800">Objection:</span>
        <p className="text-slate-600 italic">&quot;{objection}&quot;</p>
      </div>
    </div>
    <div className="flex items-start gap-3 ml-8">
      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
      <div>
        <span className="font-medium text-slate-800">Response:</span>
        <p className="text-slate-600">{response}</p>
      </div>
    </div>
  </div>
);

export default function SalesPlaybookPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 py-8">
          <Link to="/admin" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          
          <div className="flex items-center gap-4 mb-6">
            <img src={ASKISAAC_LOGO} alt="AskIsaac" className="w-16 h-16 rounded-xl" />
            <div>
              <h1 className="text-3xl font-bold">Sales & Demo Playbook</h1>
              <p className="text-blue-300">Complete guide for engaging healthcare facilities</p>
            </div>
          </div>
          
          {/* Key Message Banner */}
          <div className="bg-white/10 backdrop-blur rounded-xl p-6 border border-white/20">
            <div className="text-center mb-4">
              <span className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                🏛️ GOVERNANCE & INFRASTRUCTURE ASSET
              </span>
            </div>
            <h2 className="text-2xl font-bold text-center mb-2">Front-Line Command for Critical Environments</h2>
            <p className="text-xl text-center text-blue-200">
              <strong className="text-white">One Platform.</strong>{" "}
              <span className="text-blue-300">Supplies.</span>{" "}
              <span className="text-emerald-300">Maintenance.</span>{" "}
              <span className="text-amber-300">On Demand.</span>
            </p>
            <p className="text-center text-slate-300 mt-3 text-sm">
              Government & Private Sector Compliant • OIG/SAM.gov/OFAC Ready • HIPAA Aligned
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 py-8">
        
        {/* Other Playbooks Link */}
        <div className="mb-6 flex flex-wrap gap-4 items-center justify-center">
          <p className="text-slate-600 text-sm">Other Playbooks:</p>
          <Link 
            to="/supply-chain-playbook" 
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors text-sm font-medium"
          >
            <Package className="w-4 h-4" />
            Supply Chain Software (IMS + HART)
          </Link>
          <Link 
            to="/onboarding-playbook" 
            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg hover:bg-emerald-200 transition-colors text-sm font-medium"
          >
            <Users className="w-4 h-4" />
            Onboarding Playbook
          </Link>
        </div>
        
        {/* Who We Are */}
        <Section title="Who We Are" icon={Building2} defaultOpen={true} color="blue">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-bold text-slate-800 mb-4">AskIsaac Inc</h3>
              <p className="text-slate-600 mb-4">
                <strong>AskIsaac Inc</strong> is a standalone healthcare technology startup created by <strong>George Broughton III</strong>, 
                Founder and CEO of Sterile Mate LLC — who has been in the trenches as the backup quarterback for healthcare organizations since 2020.
              </p>
              <p className="text-slate-600 mb-4">
                When facilities couldn&apos;t find the medical devices they needed and couldn&apos;t wait for an allocation because a surgery was depending on it — <strong>we showed up</strong>. From emergency supply sourcing to managing complex Capital Projects (Water Purification Systems for Sterile Processing and Dialysis), we&apos;ve delivered when it mattered most.
              </p>
              <p className="text-slate-600 mb-4">
                AskIsaac.io brings all the players together under one hood — managing projects from start to finish.
              </p>
              
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                <h4 className="font-semibold text-blue-800 mb-2">Key Facts:</h4>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    In the trenches with healthcare since 2020
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    Capital project expertise (Water Purification, Dialysis)
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    Currently in beta with one of the largest public health systems in the US
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-blue-500" />
                    End-to-end project management under one roof
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="flex flex-col items-center justify-center">
              <img src={GEORGE_IMAGE} alt="George Broughton III" className="w-32 h-32 rounded-full mb-4 border-4 border-blue-200" />
              <h4 className="font-bold text-slate-800">George Broughton III</h4>
              <p className="text-slate-600">Founder, AskIsaac Inc</p>
              <p className="text-sm text-slate-500">Founder &amp; CEO, Sterile Mate LLC</p>
              <p className="text-xs text-slate-400 mt-2 text-center">The backup quarterback since 2020</p>
            </div>
          </div>
        </Section>

        {/* The Challenge We Address */}
        <Section title="The Challenge We Address" icon={Target} color="red">
          <div className="bg-amber-50 rounded-xl p-6 border border-amber-200 mb-6">
            <h3 className="text-xl font-bold text-amber-800 mb-4">Expanding Beyond Your Primary Network</h3>
            <p className="text-slate-700 mb-4">
              Healthcare facilities often need to expand beyond their primary supply and service networks. Whether it&apos;s sourcing specialty items, finding qualified contractors quickly, or managing capital projects — having a broader network means more options and faster response times.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <Clock className="w-6 h-6 text-amber-500 mb-2" />
                <h4 className="font-semibold text-slate-800">Time-Sensitive Needs</h4>
                <p className="text-sm text-slate-600">When surgeries depend on it, you need options fast</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <Users className="w-6 h-6 text-amber-500 mb-2" />
                <h4 className="font-semibold text-slate-800">Limited Contractor Pool</h4>
                <p className="text-sm text-slate-600">Specialized maintenance requires credentialed experts</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <FileText className="w-6 h-6 text-amber-500 mb-2" />
                <h4 className="font-semibold text-slate-800">Complex Capital Projects</h4>
                <p className="text-sm text-slate-600">Coordinating multiple vendors and contractors</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-amber-200">
                <Shield className="w-6 h-6 text-amber-500 mb-2" />
                <h4 className="font-semibold text-slate-800">Compliance Requirements</h4>
                <p className="text-sm text-slate-600">Audit trails and documentation for every transaction</p>
              </div>
            </div>
          </div>
          
          <ScriptCard 
            title="Discovery Script"
            script="Tell me about your current process for sourcing supplies outside your primary network. How do you find and vet contractors for specialized maintenance? And when you're running a capital project, how do you coordinate all the moving pieces?"
            tips="Focus on expanding their capabilities, not replacing what works."
          />
        </Section>

        {/* Our Solution */}
        <Section title="Our Solution" icon={Zap} color="green">
          <div className="space-y-6">
            {/* The Core Value Proposition */}
            <div className="bg-gradient-to-r from-emerald-50 to-cyan-50 rounded-xl p-6 border border-emerald-200">
              <h3 className="text-xl font-bold text-emerald-800 mb-4">Front-Line Empowerment for Critical Environments</h3>
              <p className="text-slate-700 mb-4">
                A hospital is a critical environment. When something breaks, it&apos;s not an inconvenience—it&apos;s a clinical risk.
                AskIsaac gives your front-line staff the power to solve problems <strong>in the moment</strong>—while giving you full visibility and compliance automatically.
              </p>
              <div className="bg-white rounded-lg p-4 border border-emerald-300">
                <p className="text-emerald-800 font-semibold italic">
                  &quot;Your front-line staff deserve tools that work as hard as they do.&quot;
                </p>
              </div>
            </div>

            {/* The 30-Second Flow */}
            <div className="bg-slate-900 rounded-xl p-6 text-white">
              <h4 className="text-lg font-bold text-cyan-400 mb-4">The 30-Second Response</h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                  <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center mb-3">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <h5 className="font-semibold text-white mb-2">Text Isaac from the Floor</h5>
                  <p className="text-sm text-slate-400">No desktop. No login. No forms. Just a message.</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                  <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center mb-3">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <h5 className="font-semibold text-white mb-2">Engine Takes Over</h5>
                  <p className="text-sm text-slate-400">Auto-routes to vetted contractors. Triggers compliance. Notifies everyone.</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                  <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center mb-3">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <h5 className="font-semibold text-white mb-2">Problem Solved</h5>
                  <p className="text-sm text-slate-400">GPS-verified arrival. Photos of work. Audit trail complete.</p>
                </div>
              </div>
            </div>
            
            {/* We Deploy, Not Just Track */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-cyan-50 rounded-xl p-5 border border-cyan-200">
                <div className="w-12 h-12 bg-cyan-600 rounded-lg flex items-center justify-center mb-3">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-slate-800 mb-2">We Deploy, Not Just Track</h4>
                <p className="text-sm text-slate-600">AskIsaac isn&apos;t another dashboard. It&apos;s a dedicated engine that knows what your team needs and dispatches pre-vetted local contractors who can act TODAY.</p>
              </div>
              
              <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
                <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-3">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-slate-800 mb-2">Everyone Under One Hood</h4>
                <p className="text-sm text-slate-600">Nurse, plumber, supplier, facilities manager—all see the same ticket, updated in real-time. No more phone tag. One source of truth.</p>
              </div>
            </div>

            {/* Platform Capabilities */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-5 border border-slate-200">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
                  <Package className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="font-bold text-slate-800 mb-2">QuickBid™ Engine</h4>
                <p className="text-sm text-slate-600">Auto-triggers competitive bidding for Texas compliance (&gt;$2,500). Get 3+ quotes in hours, not weeks.</p>
              </div>
              
              <div className="bg-white rounded-xl p-5 border border-slate-200">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-3">
                  <Truck className="w-6 h-6 text-emerald-600" />
                </div>
                <h4 className="font-bold text-slate-800 mb-2">GPS-Verified Response</h4>
                <p className="text-sm text-slate-600">Know exactly when help arrives and leaves. No more &quot;where&apos;s the tech?&quot;</p>
              </div>
              
              <div className="bg-white rounded-xl p-5 border border-slate-200">
                <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center mb-3">
                  <Shield className="w-6 h-6 text-violet-600" />
                </div>
                <h4 className="font-bold text-slate-800 mb-2">Auto-Compliance</h4>
                <p className="text-sm text-slate-600">3-quote rules, AAMI ST108, Joint Commission documentation—handled automatically.</p>
              </div>
            </div>

            {/* Multi-Stakeholder Sync & Supplier Chain of Command */}
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div className="bg-cyan-50 rounded-xl p-5 border border-cyan-200">
                <div className="w-12 h-12 bg-cyan-500 rounded-lg flex items-center justify-center mb-3">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-slate-800 mb-2">Multi-Stakeholder Sync</h4>
                <p className="text-sm text-slate-600">Tag plumbers, nurses, suppliers on any maintenance ticket. Unregistered users get invited automatically — every ticket becomes a lead generation tool.</p>
              </div>
              
              <div className="bg-amber-50 rounded-xl p-5 border border-amber-200">
                <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center mb-3">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-slate-800 mb-2">Supplier Chain of Command</h4>
                <p className="text-sm text-slate-600">4-tier role hierarchy for supplier organizations. Principals manage staff, Supervisors assign jobs, Technicians execute, Buyers approve quotes.</p>
              </div>
            </div>
            
            <ScriptCard 
              title="Solution Pitch Script"
              script="In a hospital, your front-line staff need to make decisions that count in the moment. But right now they're stuck with phone tag, paper forms, and vendor voicemail while patients wait. What if your SPD manager could text Isaac and get a response in 30 seconds—not 30 minutes? We deploy pre-vetted local contractors, trigger compliance automatically, and give you full visibility. That's AskIsaac—front-line empowerment for critical environments."
              tips="Lead with front-line pain, show the 30-second magic, then hit the back-office benefits."
            />
          </div>
        </Section>

        {/* The Bridge: Front-Line to Bottom Line */}
        <Section title="Front-Line to Bottom Line" icon={TrendingUp} color="blue">
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-xl p-6 border border-indigo-200">
              <h3 className="text-xl font-bold text-indigo-800 mb-3">The Marketing Pitch: Two Products in One</h3>
              <p className="text-lg text-slate-700 italic mb-4">
                &quot;AskIsaac: Built for front-line urgency, engineered for back-office integrity.&quot;
              </p>
              <p className="text-slate-600">
                Position the platform as both a <strong>&quot;Front-Line Concierge&quot;</strong> (on-demand support) 
                and a <strong>&quot;Back-Office Heavy Lifter&quot;</strong> (solid operations). This solves the two biggest 
                pain points in hospital administration.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Front-Line Concierge */}
              <div className="bg-cyan-50 rounded-xl p-5 border border-cyan-200">
                <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-cyan-800 mb-2">1. Front-Line Concierge</h4>
                <p className="text-sm text-slate-600 mb-3">This is your &quot;Trojan Horse&quot; for adoption. Most hospital software is designed for people at desks, but your users are mobile and time-crunched.</p>
                <div className="bg-white rounded-lg p-3 border border-cyan-100">
                  <p className="text-sm text-cyan-700 font-medium mb-1">The Hook:</p>
                  <p className="text-sm text-slate-700 italic">&quot;We support those who need us most.&quot;</p>
                </div>
                <div className="mt-3 text-sm text-slate-600">
                  <strong>The Experience:</strong> Instead of logging into a complex portal, a nurse or tech uses the Telegram bot as a concierge to trigger a project, request a courier, or report a water loop issue.
                </div>
              </div>

              {/* Back-Office Heavy Lifter */}
              <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200">
                <div className="w-12 h-12 bg-emerald-500 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <h4 className="font-bold text-emerald-800 mb-2">2. Back-Office Heavy Lifter</h4>
                <p className="text-sm text-slate-600 mb-3">While the front end is simple, the back end is &quot;solid enough to do heavy lifting.&quot; This is what C-Suite and Directors care about.</p>
                <div className="bg-white rounded-lg p-3 border border-emerald-100">
                  <p className="text-sm text-emerald-700 font-medium mb-1">The Hook:</p>
                  <p className="text-sm text-slate-700 italic">&quot;Full ERP Integration Capability.&quot;</p>
                </div>
                <div className="mt-3 text-sm text-slate-600">
                  <strong>The Experience:</strong> Data captured by the Concierge (GPS logs, photo evidence, AAMI ST108 compliance) flows directly into the hospital&apos;s existing ERP or our management portal.
                </div>
              </div>
            </div>

            {/* Audience-Specific Pitches */}
            <div className="bg-slate-100 rounded-xl p-6">
              <h4 className="font-semibold text-slate-800 mb-4">How to Sell This Duality:</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <ScriptCard 
                  title="To SPD/Lab Manager"
                  script="We give your team a digital concierge so they can spend less time on paperwork and more time on clinical outcomes."
                  tips="Focus on making their team's life easier. They deal with front-line chaos daily."
                />
                <ScriptCard 
                  title="To CFO/Facility Director"
                  script="We provide the heavy-lifting ERP integration and Geo-fenced audit trails you need to justify every dollar of infrastructure spend."
                  tips="Focus on data, compliance, and ROI justification. Speak their language."
                />
              </div>
            </div>

            {/* Why ERP is the Killer Feature */}
            <div className="bg-violet-50 rounded-xl p-6 border border-violet-200">
              <h4 className="font-semibold text-violet-800 mb-3 flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Why &quot;ERP Integration&quot; is the Killer Feature
              </h4>
              <p className="text-slate-700 mb-4">
                By highlighting ERP capability, you aren&apos;t just selling another &quot;app.&quot; You are selling a <strong>system of record</strong>. 
                When AskIsaac talks to their existing ERP (like SAP, Oracle, or Infor), you become a permanent part of their 
                hospital&apos;s digital nervous system. It makes the platform much harder to replace later.
              </p>
              <div className="grid md:grid-cols-3 gap-3">
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-sm font-medium text-violet-700">Purchase Requisitions</p>
                  <p className="text-xs text-slate-500">Auto-creates PRs in ERP</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-sm font-medium text-violet-700">Invoice Automation</p>
                  <p className="text-xs text-slate-500">Direct flow to AP systems</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center">
                  <p className="text-sm font-medium text-violet-700">Compliance Audit Trail</p>
                  <p className="text-xs text-slate-500">GPS + photos linked to WOs</p>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* NEW: Nationwide Recruitment System */}
        <Section title="🚀 GAME-CHANGER: Nationwide Recruitment System" icon={MapPin} color="green">
          <div className="space-y-6">
            {/* Hero Banner */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center border border-white/30">
                  <Database className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">Lead Generation on Autopilot</h3>
                  <p className="text-emerald-200">Access 50,000+ Licensed Contractors & FDA-Registered Suppliers</p>
                </div>
              </div>
              <p className="text-white/90 text-lg">
                AskIsaac puts you <strong>in the driver&apos;s seat</strong> with direct access to public state licensing board records 
                and the FDA&apos;s nationwide medical device registration database. Search, filter, and invite contractors and suppliers 
                to join your network — all from one dashboard.
              </p>
            </div>

            {/* Contractor Recruitment */}
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <h4 className="text-lg font-bold text-blue-800 mb-4 flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                Contractor Recruitment Dashboard
              </h4>
              <p className="text-slate-700 mb-4">
                Search public state licensing databases to find licensed plumbers, electricians, HVAC techs, and general contractors. 
                Get full contact information and license verification in real-time.
              </p>
              
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="font-semibold text-slate-800">Washington</span>
                  </div>
                  <p className="text-sm text-slate-600">Full Data: Name, Address, Phone, License Status, Specialty</p>
                  <p className="text-xs text-emerald-600 font-medium mt-1">✓ Excellent Data Quality</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="font-semibold text-slate-800">Oregon</span>
                  </div>
                  <p className="text-sm text-slate-600">Full Data: Name, Address, Phone, License, Bond & Insurance Info</p>
                  <p className="text-xs text-emerald-600 font-medium mt-1">✓ Excellent Data Quality</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                      <AlertTriangle className="w-4 h-4 text-amber-600" />
                    </div>
                    <span className="font-semibold text-slate-800">Texas</span>
                  </div>
                  <p className="text-sm text-slate-600">Limited: License Number, Type, Status only</p>
                  <p className="text-xs text-amber-600 font-medium mt-1">⚠ Limited Data (TDLR restrictions)</p>
                </div>
              </div>

              <div className="bg-slate-900 rounded-lg p-4 text-white">
                <p className="text-sm font-medium text-cyan-400 mb-2">🔮 Coming Soon: California, Florida, New York, Arizona, Colorado, Georgia, NC, NJ, PA, OH</p>
                <p className="text-xs text-slate-400">Expanding to all 50 states with publicly available licensing data</p>
              </div>
            </div>

            {/* Medical Device Supplier Recruitment */}
            <div className="bg-violet-50 rounded-xl p-6 border border-violet-200">
              <h4 className="text-lg font-bold text-violet-800 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5" />
                Medical Device Supplier Recruitment Dashboard
              </h4>
              <p className="text-slate-700 mb-4">
                Search the FDA&apos;s openFDA database for registered medical device manufacturers, distributors, and repackagers. 
                <strong> Nationwide coverage</strong> — all 50 US states available.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <div className="bg-white rounded-lg p-4 border border-violet-200">
                  <h5 className="font-semibold text-slate-800 mb-2">Search Filters</h5>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• State (all 50 US states)</li>
                    <li>• City or ZIP code</li>
                    <li>• Company name</li>
                    <li>• Establishment type (Manufacturer, Distributor, etc.)</li>
                  </ul>
                </div>
                <div className="bg-white rounded-lg p-4 border border-violet-200">
                  <h5 className="font-semibold text-slate-800 mb-2">Data Returned</h5>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• FEI Number (FDA Establishment ID)</li>
                    <li>• Full Address & Contact Info</li>
                    <li>• Establishment Types</li>
                    <li>• Product Codes (device categories)</li>
                  </ul>
                </div>
              </div>

              <div className="bg-gradient-to-r from-violet-600 to-purple-600 rounded-lg p-4 text-white">
                <p className="font-medium">💡 Sales Tip: This is your &quot;secret weapon&quot; for supplier outreach. Instead of cold-calling random companies, 
                you can target FDA-registered suppliers in specific regions who already handle medical devices.</p>
              </div>
            </div>

            {/* Demo Scripts */}
            <div className="space-y-4">
              <ScriptCard 
                title="Recruitment Feature Pitch Script"
                script="One of the most powerful features we've built is our Nationwide Recruitment System. Instead of waiting for contractors and suppliers to find you, AskIsaac lets you search public state licensing databases and the FDA's device registration database to find and invite them directly. You can search by location, specialty, license status — and see full contact information. It's like having a lead generation team working 24/7, but with verified, publicly-sourced data."
                tips="Emphasize 'lead generation on autopilot' and 'you're in the driver's seat'. This is a game-changer for network expansion."
              />
              
              <ScriptCard 
                title="ROI Script for Recruitment"
                script="Think about how much time your team spends finding and vetting new contractors. With our recruitment dashboard, you can search 50,000+ licensed professionals across multiple states in seconds. Each invite costs you nothing — we handle the outreach. When they join and complete jobs, that's when you see the ROI. One good contractor relationship can be worth tens of thousands in fulfilled work orders."
                tips="Connect to time savings and network growth. Emphasize zero-cost lead generation."
              />

              <ScriptCard 
                title="🔥 Critical Response Positioning Script (NEW)"
                script="Here's the key difference: We're not a planned-procurement bidding portal. Those platforms are built for scheduled purchases — submit an RFP, wait 2-4 weeks for bids, then another week for evaluation. But when your OR is flooded, when sterilizers are down, when a surgery is waiting — you need someone qualified and available NOW. AskIsaac is a critical response network. First qualified responder wins. No bidding wars. Hours, not weeks."
                tips="This is your main differentiator. Use it early in the conversation to set the frame. Hospitals understand emergencies — speak their language."
              />

              <ScriptCard 
                title="The Contractor Invitation Pitch"
                script="When we invite contractors to join the network, we tell them: 'You're not bidding. You're responding to emergencies.' Healthcare facilities can't wait for a 2-week procurement process. They need someone who can respond in hours. If you're qualified and available, you win the job. First responder advantage. That's why contractors love this — no more racing to the bottom on price."
                tips="Use this when explaining how the contractor side works. It reinforces the 'critical response' positioning from both sides."
              />
            </div>

            {/* Quick Demo Access */}
            <div className="flex gap-4">
              <Link to="/contractor/recruitment" className="flex-1">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  <Wrench className="w-4 h-4 mr-2" />
                  Demo Contractor Search
                </Button>
              </Link>
              <Link to="/supplier/recruitment" className="flex-1">
                <Button className="w-full bg-violet-600 hover:bg-violet-700">
                  <Package className="w-4 h-4 mr-2" />
                  Demo Supplier Search
                </Button>
              </Link>
            </div>

            {/* Link to Join Network Page */}
            <div className="mt-4 bg-red-50 rounded-xl p-4 border border-red-200">
              <p className="text-red-800 font-medium mb-2">📧 Contractor/Supplier Recruitment Landing Page:</p>
              <p className="text-slate-600 text-sm mb-3">
                When you send invite emails from the recruitment dashboard, contractors land on this page with the "Hospital Needs You NOW" messaging.
              </p>
              <Link to="/join-network?type=contractor">
                <Button variant="outline" className="border-red-300 text-red-700 hover:bg-red-100">
                  Preview Join Network Page →
                </Button>
              </Link>
            </div>
          </div>
        </Section>

        {/* Why Choose Us */}
        <Section title="Why Choose Us" icon={Star} color="purple">
          <div className="space-y-4">
            <div className="bg-violet-50 rounded-xl p-6 border border-violet-200">
              <h3 className="text-lg font-bold text-violet-800 mb-4">Competitive Differentiators</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-violet-600 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-800">Built by Healthcare Insiders</h4>
                    <p className="text-sm text-slate-600">6 years in Texas Medical Center, not Silicon Valley</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-violet-600 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-800">Compliance-First</h4>
                    <p className="text-sm text-slate-600">Auto-triggers 3-quote process for Texas law</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-violet-600 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-800">Real-Time Visibility</h4>
                    <p className="text-sm text-slate-600">GPS tracking + live status updates</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-violet-600 mt-1" />
                  <div>
                    <h4 className="font-semibold text-slate-800">Zero Long-Term Contracts</h4>
                    <p className="text-sm text-slate-600">Pay per use, cancel anytime</p>
                  </div>
                </div>
              </div>
            </div>
            
            <ScriptCard 
              title="Differentiation Script"
              script="Unlike traditional vendors who just take orders, we're an orchestration platform. Think of us as the Uber for healthcare operations — we connect you with the right suppliers and contractors, handle the compliance paperwork, and give you real-time visibility into every job."
              tips="Use the Uber analogy — everyone understands it immediately."
            />
          </div>
        </Section>

        {/* Demo Flow */}
        <Section title="Demo Flow & Script" icon={Play} color="blue">
          <div className="space-y-6">
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <h3 className="text-lg font-bold text-blue-800 mb-4">Recommended Demo Order (18 minutes)</h3>
              <div className="space-y-4">
                {[
                  { step: 1, title: "Show the Problem", time: "2 min", desc: "Start with the $500K slide, ask about their pain points" },
                  { step: 2, title: "Hospital Portal", time: "3 min", desc: "Submit a backorder request, show how fast it flows through" },
                  { step: 3, title: "QuickBid Engine", time: "2 min", desc: "Show automated 3-quote process for compliance" },
                  { step: 4, title: "🚀 Recruitment Dashboards", time: "4 min", desc: "GAME-CHANGER: Search 50K+ contractors & FDA suppliers. Show live searches in WA/OR." },
                  { step: 5, title: "Fleet Tracking", time: "2 min", desc: "Show real-time GPS tracking dashboard" },
                  { step: 6, title: "Close", time: "3 min", desc: "Beta invitation, pricing discussion, next steps" }
                ].map((item) => (
                  <div key={item.step} className="flex items-center gap-4 bg-white rounded-lg p-4 border border-blue-200">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-slate-800">{item.title}</h4>
                        <span className="text-sm text-blue-600 font-medium">{item.time}</span>
                      </div>
                      <p className="text-sm text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Link to="/demo" className="flex-1">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  <Play className="w-4 h-4 mr-2" />
                  Demo Environment
                </Button>
              </Link>
              <Link to="/hospital-presentation" className="flex-1">
                <Button variant="outline" className="w-full">
                  <FileText className="w-4 h-4 mr-2" />
                  Hospital Presentation
                </Button>
              </Link>
              <Link to="/contractor/recruitment" className="flex-1">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                  <Wrench className="w-4 h-4 mr-2" />
                  Contractor Search
                </Button>
              </Link>
              <Link to="/supplier/recruitment" className="flex-1">
                <Button className="w-full bg-violet-600 hover:bg-violet-700">
                  <Package className="w-4 h-4 mr-2" />
                  Supplier Search
                </Button>
              </Link>
            </div>
          </div>
        </Section>

        {/* Objection Handling */}
        <Section title="Objection Handling" icon={Shield} color="orange">
          <div className="space-y-4">
            <ObjectionHandler 
              objection="We already have a GPO contract"
              response="Absolutely — and we're here to enhance that relationship, not replace it. AskIsaac expands your network with additional suppliers and contractors for specialized needs, capital projects, or when you need faster response times. Think of us as adding depth to your bench."
            />
            <ObjectionHandler 
              objection="We don't have budget for new systems"
              response="AskIsaac doesn't require upfront investment. You only pay when you use us — just like Uber. No monthly fees, no long-term contracts. We estimate the average facility loses $500K annually to downtime. Even preventing 10% of that pays for our service many times over."
            />
            <ObjectionHandler 
              objection="Our team is too busy to learn a new system"
              response="That's exactly why we built it the way we did. Hospital staff can submit requests via Telegram — just text a message. No training required. It's as simple as sending a text to a friend."
            />
            <ObjectionHandler 
              objection="How do I know your contractors are qualified?"
              response="Every contractor and supplier on our platform is pre-vetted with verified credentials, insurance, and healthcare certifications. We maintain AAMI ST108 compliance standards, and you can see their credentials before approving any quote."
            />
          </div>
        </Section>

        {/* Pricing */}
        <Section title="Pricing & Business Model" icon={DollarSign} color="green">
          <div className="space-y-6">
            {/* Value Proposition */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
              <h3 className="text-xl font-bold mb-3">Simple, Transparent Pricing</h3>
              <p className="text-emerald-100 text-lg">
                No subscription fees. No setup costs. No hidden charges. Hospitals pay only when they use the service—
                with pricing built into each quoted request. You see the final price before you approve.
              </p>
            </div>

            {/* 3-Tier Response System */}
            <div className="bg-white rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-emerald-600" />
                3-Tier Response System
              </h3>
              <p className="text-slate-600 mb-6">
                Choose your response tier based on urgency. Faster response = premium pricing. You always know the cost upfront.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                {/* Standard */}
                <div className="bg-blue-50 rounded-xl p-5 border-2 border-blue-200">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                    <span className="font-bold text-blue-800">STANDARD</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-600 mb-2">24-48 Hours</div>
                  <p className="text-sm text-slate-600 mb-3">Response time for routine requests</p>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Routine supply orders</li>
                    <li>• Scheduled maintenance</li>
                    <li>• Non-urgent backorders</li>
                  </ul>
                </div>
                {/* Priority */}
                <div className="bg-amber-50 rounded-xl p-5 border-2 border-amber-200">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                    <span className="font-bold text-amber-800">PRIORITY</span>
                  </div>
                  <div className="text-2xl font-bold text-amber-600 mb-2">4-8 Hours</div>
                  <p className="text-sm text-slate-600 mb-3">Priority handling for urgent needs</p>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Urgent equipment repairs</li>
                    <li>• Same-day supply needs</li>
                    <li>• Time-sensitive projects</li>
                  </ul>
                </div>
                {/* STAT */}
                <div className="bg-red-50 rounded-xl p-5 border-2 border-red-300">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span className="font-bold text-red-800">STAT</span>
                  </div>
                  <div className="text-2xl font-bold text-red-600 mb-2">2 Hours</div>
                  <p className="text-sm text-slate-600 mb-3">Emergency response for critical needs</p>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• OR/ER emergencies</li>
                    <li>• Surgery-critical supplies</li>
                    <li>• Equipment down situations</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* How It Works */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <h3 className="text-lg font-bold text-slate-800 mb-4">How Pricing Works</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Package className="w-5 h-5 text-emerald-600" />
                    </div>
                    <h4 className="font-semibold text-slate-800">Supplies & Backorders</h4>
                  </div>
                  <p className="text-sm text-slate-600">
                    We source from our verified supplier network. You receive a quoted price per item. 
                    Approve and we deliver. Pricing varies by tier and availability.
                  </p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Wrench className="w-5 h-5 text-blue-600" />
                    </div>
                    <h4 className="font-semibold text-slate-800">Maintenance & Services</h4>
                  </div>
                  <p className="text-sm text-slate-600">
                    Contractors bid on your jobs. You see total project cost before approval. 
                    We manage the vendor, you manage your equipment.
                  </p>
                </div>
              </div>
            </div>

            {/* Key Message */}
            <div className="bg-emerald-50 rounded-xl p-5 border border-emerald-200">
              <p className="text-emerald-800 font-medium text-center">
                <strong>Bottom Line:</strong> You always see the final price before you approve. 
                No surprises. No subscriptions. Pay only when you use the service.
              </p>
            </div>
          </div>
        </Section>

        {/* Call to Action */}
        <Section title="Closing & Next Steps" icon={Calendar} color="blue">
          <div className="space-y-6">
            <ScriptCard 
              title="Closing Script"
              script="Based on what you've shared, it sounds like AskIsaac could save your team significant time and prevent costly downtime. We're currently onboarding beta partners in the Texas Medical Center region. Would you be interested in being one of our founding hospital partners? We can have you live within a week."
              tips="Always ask for the commitment. Don't end the call without a clear next step."
            />
            
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <h3 className="text-lg font-bold text-blue-800 mb-4">Next Steps Checklist</h3>
              <div className="space-y-3">
                {[
                  "Schedule follow-up call with decision maker",
                  "Send beta invitation email (from Email Campaigns)",
                  "Add to CRM pipeline",
                  "Set reminder for 48-hour follow-up",
                  "Send hospital presentation link"
                ].map((item, idx) => (
                  <label key={idx} className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" className="w-5 h-5 rounded border-slate-300" />
                    <span className="text-slate-700">{item}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="flex gap-4">
              <Link to="/admin/email-campaigns" className="flex-1">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700">
                  <Mail className="w-4 h-4 mr-2" />
                  Send Beta Invitation
                </Button>
              </Link>
              <Link to="/request-demo" className="flex-1">
                <Button variant="outline" className="w-full">
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule Demo
                </Button>
              </Link>
            </div>
          </div>
        </Section>

        {/* CleanRead ViB Product Section */}
        <Section title="Product Spotlight: CleanRead™ ViB Borescope" icon={Target} color="purple">
          <div className="space-y-6">
            {/* Product Hero */}
            <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-xl p-6 border border-violet-200">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-violet-600 rounded-lg flex items-center justify-center">
                      <Target className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-violet-800">CleanRead™ ViB</h3>
                      <p className="text-violet-600">AI Visual Inspection Borescope</p>
                    </div>
                  </div>
                  <p className="text-slate-700 mb-4">
                    The <strong>Next-Gen AI Visual Inspection Borescope</strong> helps Sterile Processing departments verify 
                    effective cleaning, detect debris, discoloration, or damage inside endoscopes and cannulated instruments—
                    supporting patient safety, compliance, and instrument longevity.
                  </p>
                  <div className="flex gap-3">
                    <a 
                      href="https://sterilemate.com/next-gen-ai-visual-inspection-borescope/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Product Page
                    </a>
                    <a 
                      href="https://customer-assets.emergentagent.com/job_c4eb42da-4586-4303-b844-90350e5cf110/artifacts/j668xdj0_clearead-vib-ifu.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white text-violet-700 border border-violet-300 rounded-lg hover:bg-violet-50 transition-colors"
                    >
                      <FileText className="w-4 h-4" />
                      IFU Brochure
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Features */}
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center mb-3">
                  <Zap className="w-5 h-5 text-violet-600" />
                </div>
                <h4 className="font-semibold text-slate-800 mb-1">ONE-PASS Camera Wand</h4>
                <p className="text-sm text-slate-600">Reaches distal tip through suction valve for full internal inspection</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center mb-3">
                  <Shield className="w-5 h-5 text-cyan-600" />
                </div>
                <h4 className="font-semibold text-slate-800 mb-1">Auto Brightness Control</h4>
                <p className="text-sm text-slate-600">Automatically adjusts illumination for consistent clarity</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mb-3">
                  <Target className="w-5 h-5 text-emerald-600" />
                </div>
                <h4 className="font-semibold text-slate-800 mb-1">HD Imaging</h4>
                <p className="text-sm text-slate-600">120° field of view &amp; 400×400 resolution for clear visualization</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-slate-200">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center mb-3">
                  <Wrench className="w-5 h-5 text-amber-600" />
                </div>
                <h4 className="font-semibold text-slate-800 mb-1">Waterproof Wand</h4>
                <p className="text-sm text-slate-600">Fully sealed, easy to clean for fast turnaround between inspections</p>
              </div>
            </div>

            {/* Technical Specifications */}
            <div className="bg-slate-900 rounded-xl p-6 text-white">
              <h4 className="text-lg font-bold text-cyan-400 mb-4">Technical Specifications</h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                  <p className="text-xs text-slate-400 uppercase mb-1">Field of View</p>
                  <p className="text-2xl font-bold text-white">120°</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                  <p className="text-xs text-slate-400 uppercase mb-1">Resolution</p>
                  <p className="text-2xl font-bold text-white">400×400</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                  <p className="text-xs text-slate-400 uppercase mb-1">Wand Type</p>
                  <p className="text-2xl font-bold text-white">Waterproof</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-700">
                <h5 className="font-semibold text-white mb-2">Package Contents:</h5>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-slate-300">
                  <p>• All-in-one Computer</p>
                  <p>• AC Adapter for Computer</p>
                  <p>• Visual Inspection Borescope</p>
                  <p>• Borescope Controller</p>
                  <p>• Power Cable for Controller</p>
                  <p>• Controller Casing</p>
                </div>
              </div>
            </div>

            {/* Inspection Capabilities */}
            <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
              <h4 className="font-semibold text-amber-800 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                What It Detects
              </h4>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-3 text-center border border-amber-200">
                  <p className="font-semibold text-amber-700">Debris</p>
                  <p className="text-xs text-slate-500">Residue or particles in channels</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center border border-amber-200">
                  <p className="font-semibold text-amber-700">Discoloration</p>
                  <p className="text-xs text-slate-500">Staining or corrosion signs</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center border border-amber-200">
                  <p className="font-semibold text-amber-700">Tears</p>
                  <p className="text-xs text-slate-500">Internal damage or rips</p>
                </div>
                <div className="bg-white rounded-lg p-3 text-center border border-amber-200">
                  <p className="font-semibold text-amber-700">Scratches</p>
                  <p className="text-xs text-slate-500">Surface abrasions inside</p>
                </div>
              </div>
            </div>

            {/* Sales Scripts */}
            <div className="space-y-4">
              <ScriptCard 
                title="Discovery Script for SPD Managers"
                script="How are you currently validating that your endoscopes are clean before they go to the autoclave? Are you relying on visual inspection alone, or do you have a borescope system? And if something gets missed—how does that impact your reprocessing cycle?"
                tips="Lead with the pain of not knowing what's inside the scope. Joint Commission requires documented inspection."
              />
              
              <ScriptCard 
                title="Product Pitch Script"
                script="The CleanRead ViB is a next-gen visual inspection borescope designed specifically for Sterile Processing. With a ONE-PASS camera wand that reaches the distal tip through the suction valve, your techs can verify cleaning and catch debris, discoloration, or damage before it becomes a patient safety issue. The 120-degree field of view and HD resolution mean nothing hides. And it integrates with ATP testing for a complete QA workflow."
                tips="Emphasize patient safety, compliance documentation, and workflow integration."
              />

              <ScriptCard 
                title="ROI Justification Script"
                script="A single scope repair can cost $15,000 to $50,000. If the CleanRead catches just one issue before it becomes critical damage, it pays for itself. Plus, you're protecting patients from potential cross-contamination that could lead to HAIs. The documentation alone gives your facility audit-ready proof of due diligence."
                tips="Always connect to patient safety first, then cost savings. CFOs care about both."
              />
            </div>

            {/* Quick Workflow */}
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <h4 className="font-semibold text-blue-800 mb-4">Inspection Workflow (Demo Script)</h4>
              <div className="space-y-3">
                {[
                  { step: 1, title: "Preparation", desc: "Scope fully cleaned at sink, connectors removed, moved to inspection station" },
                  { step: 2, title: "System Setup", desc: "Connect ViB controller via USB, login to VIB software, start New Procedure" },
                  { step: 3, title: "ATP Integration", desc: "Run inside channel test with ATP handheld while connected to Wi-Fi" },
                  { step: 4, title: "Visual Inspection", desc: "Position distal tip at channel opening, advance with short controlled strokes" },
                  { step: 5, title: "Documentation", desc: "Watch feed for issues, press GREEN button to capture images for compliance" },
                  { step: 6, title: "Clean & Store", desc: "Remove wand with disinfectant wipe, store in housing" }
                ].map((item) => (
                  <div key={item.step} className="flex items-center gap-4 bg-white rounded-lg p-3 border border-blue-200">
                    <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                      {item.step}
                    </div>
                    <div className="flex-1">
                      <h5 className="font-semibold text-slate-800">{item.title}</h5>
                      <p className="text-sm text-slate-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Objection Handlers */}
            <div className="space-y-3">
              <h4 className="font-semibold text-slate-800">Common Objections</h4>
              <ObjectionHandler 
                objection="We already have a borescope"
                response="Great—that shows you understand the importance of visual inspection. How old is your current system? The CleanRead ViB offers AI-enhanced imaging with 120° field of view and integrates with ATP testing for a complete QA workflow. Plus, our ONE-PASS wand technology means faster inspections with better coverage."
              />
              <ObjectionHandler 
                objection="Our technicians don't have time for this"
                response="That's exactly why we designed the ONE-PASS system. Unlike traditional borescopes that require multiple passes, CleanRead reaches the distal tip in one motion. Most inspections take under 2 minutes. And the time you save by catching issues early—before they become expensive repairs or reprocessing failures—far outweighs the inspection time."
              />
              <ObjectionHandler 
                objection="How is this different from other borescopes?"
                response="Three key differences: First, the ONE-PASS wand reaches the distal tip through the suction valve—no complicated routing. Second, auto-brightness control means consistent image quality without manual adjustment. Third, it integrates with ATP testing, so you get a complete documentation package for Joint Commission compliance."
              />
            </div>
          </div>
        </Section>

        {/* CleanRead ATP Handheld - Live Demo Lead Presentation */}
        <Section title="🎯 Live Demo: CleanRead™ Handheld ATP Testing Device" icon={Target} color="green">
          <div className="space-y-8">
            {/* Hero Banner with Product Image */}
            <div className="relative overflow-hidden rounded-2xl">
              <img 
                src="https://sterilemate.com/wp-content/uploads/2025/12/hand-held-banner.webp" 
                alt="CleanRead Handheld ATP Testing Device" 
                className="w-full h-64 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 via-emerald-800/70 to-transparent flex items-center">
                <div className="p-8">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center border border-white/30">
                      <Zap className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">CleanRead™ Handheld</h3>
                      <p className="text-emerald-200">ATP Contamination Monitoring System</p>
                    </div>
                  </div>
                  <p className="text-white/90 text-lg max-w-xl mb-4">
                    <strong>15-Second Results.</strong> Bioluminescence technology for real-time cleanliness verification.
                  </p>
                  <div className="flex gap-3">
                    <a 
                      href="https://sterilemate.com/cleanread-handheld-atp-testing-device/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors font-medium"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Product Page
                    </a>
                    <a 
                      href="https://customer-assets.emergentagent.com/job_c4eb42da-4586-4303-b844-90350e5cf110/artifacts/x43hu4wk_CleanReadHandheld-BvXHlfsC.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-700 text-white border border-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors font-medium"
                    >
                      <FileText className="w-4 h-4" />
                      Brochure PDF
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* The Hook - Opening Statement */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Play className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-2">🎬 Opening Hook (Use This to Start Your Demo)</h4>
                  <p className="text-white/90 text-lg italic">
                    "Let me show you how to know—with certainty—whether that scope is clean before it touches your next patient. 
                    In just 15 seconds, you'll have documented proof of cleaning efficacy that satisfies Joint Commission and protects your patients."
                  </p>
                </div>
              </div>
            </div>

            {/* Product Gallery */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="rounded-xl overflow-hidden border border-slate-200 shadow-lg">
                <img 
                  src="https://sterilemate.com/wp-content/uploads/2025/12/cleanread-01-1.webp" 
                  alt="CleanRead Handheld Device - Front View" 
                  className="w-full h-48 object-cover"
                />
                <div className="p-3 bg-white">
                  <p className="text-sm font-medium text-slate-700">5" Touchscreen Interface</p>
                  <p className="text-xs text-slate-500">Works even with gloves on</p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden border border-slate-200 shadow-lg">
                <img 
                  src="https://sterilemate.com/wp-content/uploads/2025/12/cleanread-02-1.webp" 
                  alt="CleanRead Handheld Device - Side View" 
                  className="w-full h-48 object-cover"
                />
                <div className="p-3 bg-white">
                  <p className="text-sm font-medium text-slate-700">Portable Smart Device</p>
                  <p className="text-xs text-slate-500">Use anywhere in facility</p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden border border-slate-200 shadow-lg">
                <img 
                  src="https://sterilemate.com/wp-content/uploads/2025/12/hand-held-banner-1.webp" 
                  alt="CleanRead System in Action" 
                  className="w-full h-48 object-cover"
                />
                <div className="p-3 bg-white">
                  <p className="text-sm font-medium text-slate-700">Complete System</p>
                  <p className="text-xs text-slate-500">Device + Swabs + Cloud Portal</p>
                </div>
              </div>
            </div>

            {/* The Problem (Pain Points) */}
            <div className="bg-red-50 rounded-xl p-6 border border-red-200">
              <h4 className="text-lg font-bold text-red-800 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                The Problem: "How Clean Is Clean?"
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 border border-red-200">
                  <p className="font-semibold text-red-700 mb-2">Visual inspection alone fails</p>
                  <p className="text-sm text-slate-600">ATP, bioburden, and residues are invisible to the naked eye. You can't see what's actually left behind.</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-red-200">
                  <p className="font-semibold text-red-700 mb-2">Traditional testing is slow</p>
                  <p className="text-sm text-slate-600">Lab cultures take 24-48 hours. By then, the instrument has been reprocessed and used on patients.</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-red-200">
                  <p className="font-semibold text-red-700 mb-2">No documentation = liability</p>
                  <p className="text-sm text-slate-600">Without objective proof of cleaning efficacy, you're exposed to HAIs, regulatory findings, and lawsuits.</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-red-200">
                  <p className="font-semibold text-red-700 mb-2">Inconsistent protocols</p>
                  <p className="text-sm text-slate-600">Different techs clean differently. Without measurable standards, quality varies shift to shift.</p>
                </div>
              </div>
            </div>

            {/* The Solution - Key Features */}
            <div className="space-y-4">
              <h4 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                The Solution: CleanRead™ ATP Testing
              </h4>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                    <Clock className="w-6 h-6 text-emerald-600" />
                  </div>
                  <h5 className="font-bold text-slate-800 mb-2">15-Second Results</h5>
                  <p className="text-sm text-slate-600">Instant contamination detection using bioluminescence. Know immediately if cleaning was effective.</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                    <Shield className="w-6 h-6 text-cyan-600" />
                  </div>
                  <h5 className="font-bold text-slate-800 mb-2">ATP Detection</h5>
                  <p className="text-sm text-slate-600">Detects Adenosine Triphosphate from blood, tissue, bacteria, yeast, and mold—all sources of contamination.</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center mb-4">
                    <TrendingUp className="w-6 h-6 text-violet-600" />
                  </div>
                  <h5 className="font-bold text-slate-800 mb-2">Cloud Portal</h5>
                  <p className="text-sm text-slate-600">All results sync to a web dashboard for reporting, trending, and compliance documentation.</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
                    <Package className="w-6 h-6 text-amber-600" />
                  </div>
                  <h5 className="font-bold text-slate-800 mb-2">RFID & Barcode</h5>
                  <p className="text-sm text-slate-600">Scan equipment barcodes to instantly associate tests with specific instruments for tracking.</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Zap className="w-6 h-6 text-blue-600" />
                  </div>
                  <h5 className="font-bold text-slate-800 mb-2">Offline Mode</h5>
                  <p className="text-sm text-slate-600">Test anywhere—data auto-syncs to cloud when Wi-Fi becomes available. Never lose a result.</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-slate-200 hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center mb-4">
                    <Calendar className="w-6 h-6 text-rose-600" />
                  </div>
                  <h5 className="font-bold text-slate-800 mb-2">AI Test Scheduling</h5>
                  <p className="text-sm text-slate-600">Program daily testing schedules from the dashboard. Ensure consistent, compliant testing protocols.</p>
                </div>
              </div>
            </div>

            {/* Live Demo Workflow */}
            <div className="bg-slate-900 rounded-xl p-6 text-white">
              <h4 className="text-lg font-bold text-emerald-400 mb-6 flex items-center gap-2">
                <Play className="w-5 h-5" />
                🎯 LIVE DEMO: 10-Step Walkthrough
              </h4>
              <p className="text-slate-400 mb-6 italic">"Let me walk you through exactly how your tech would use this..."</p>
              
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { step: 1, title: "Dashboard Home", desc: "Tap Dashboard to see Today's Plan, Test Points, and Reports", tip: "Show the intuitive interface—works with gloves" },
                  { step: 2, title: "Select Test Group", desc: "Search by scrolling, typing, or scanning barcode/RFID", tip: "Emphasize speed—no manual lookup needed" },
                  { step: 3, title: "Select Test Point", desc: "Tap desired test point and press 'Start'", tip: "Point out the visual library of instruments" },
                  { step: 4, title: "Swab Sampling", desc: "Swab ~10cm² area with horizontal & vertical strokes", tip: "Demo the proper technique—twirl during sampling" },
                  { step: 5, title: "Prepare Swab", desc: "Place swab back in tube, break blue nib against bulb wall", tip: "Show the snap—this activates the reagent" },
                  { step: 6, title: "Mix Reagent", desc: "Squeeze and shake gently for 5 seconds", tip: "Ensures reagent contacts the swab tip" },
                  { step: 7, title: "Insert Swab", desc: "Open device lid, insert tube fully into chamber", tip: "Simple insertion—no special training needed" },
                  { step: 8, title: "Initiate Test", desc: "Close lid and press 'OK' to start", tip: "Now the magic happens—bioluminescence!" },
                  { step: 9, title: "View Results", desc: "15 seconds → Remove swab → See Pass/Fail with RLU", tip: "THIS is your compliance documentation" },
                  { step: 10, title: "Auto-Sync", desc: "Results automatically upload to cloud portal", tip: "No manual data entry—instant audit trail" }
                ].map((item) => (
                  <div key={item.step} className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                        {item.step}
                      </div>
                      <div className="flex-1">
                        <h5 className="font-semibold text-white">{item.title}</h5>
                        <p className="text-sm text-slate-300 mb-2">{item.desc}</p>
                        <p className="text-xs text-emerald-400 italic">💡 {item.tip}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Applications - Where to Use */}
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <h4 className="font-bold text-blue-800 mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Where to Test (Demo Talking Points)
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { area: "Endoscopes", icon: "🔬" },
                  { area: "Surgical Instruments", icon: "⚕️" },
                  { area: "Washer-Disinfectors", icon: "🧼" },
                  { area: "OR Tables", icon: "🏥" },
                  { area: "Bed Rails", icon: "🛏️" },
                  { area: "BP Cuffs", icon: "💉" },
                  { area: "Computer Keyboards", icon: "⌨️" },
                  { area: "Door Handles", icon: "🚪" },
                  { area: "Waiting Areas", icon: "🪑" },
                  { area: "Restrooms", icon: "🚿" },
                  { area: "Exam Rooms", icon: "📋" },
                  { area: "GI Lab Equipment", icon: "🔍" }
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-lg p-3 text-center border border-blue-200">
                    <span className="text-2xl">{item.icon}</span>
                    <p className="text-sm font-medium text-slate-700 mt-1">{item.area}</p>
                  </div>
                ))}
              </div>
              <p className="text-sm text-blue-700 mt-4 italic">
                "Anywhere you need documented proof of cleaning—from critical scopes to high-touch surfaces."
              </p>
            </div>

            {/* ROI Calculator */}
            <div className="bg-gradient-to-r from-emerald-900 to-teal-800 rounded-xl p-6 text-white">
              <h4 className="text-lg font-bold text-emerald-300 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                ROI Justification Script
              </h4>
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white/10 rounded-lg p-4 text-center backdrop-blur">
                  <p className="text-3xl font-bold text-white">$50K+</p>
                  <p className="text-emerald-200 text-sm">Cost of single HAI lawsuit</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4 text-center backdrop-blur">
                  <p className="text-3xl font-bold text-white">$15K-$50K</p>
                  <p className="text-emerald-200 text-sm">Scope repair from damage</p>
                </div>
                <div className="bg-white/10 rounded-lg p-4 text-center backdrop-blur">
                  <p className="text-3xl font-bold text-white">15 sec</p>
                  <p className="text-emerald-200 text-sm">Time to verify cleaning</p>
                </div>
              </div>
              <div className="bg-white/5 rounded-lg p-4 border border-white/20">
                <p className="text-white/90 italic">
                  "If CleanRead catches just ONE contaminated scope before it reaches a patient, you've avoided a potential HAI, 
                  a regulatory finding, and protected your facility from liability. The system pays for itself with a single save. 
                  And every test creates documented proof of due diligence for Joint Commission."
                </p>
              </div>
            </div>

            {/* Sales Scripts */}
            <div className="space-y-4">
              <h4 className="text-lg font-bold text-slate-800">📝 Demo Presentation Scripts</h4>
              
              <ScriptCard 
                title="Opening Hook (30 seconds)"
                script="How do you currently verify that a scope is clean BEFORE it goes to the autoclave? Visual inspection? The problem is—ATP, bioburden, blood residue—they're invisible. You can't see what you're missing. Let me show you how CleanRead gives you certainty in 15 seconds."
                tips="Start with their current gap. Make them feel the uncertainty."
              />
              
              <ScriptCard 
                title="The Demo Pitch (60 seconds)"
                script="CleanRead is a handheld ATP testing system that uses bioluminescence to detect contamination at the cellular level. Your tech swabs the surface, inserts the tube, and in 15 seconds—you have a Pass or Fail with an RLU reading. That result instantly syncs to our cloud portal, creating a permanent compliance record. Joint Commission loves this—documented proof of cleaning efficacy, timestamped and tracked."
                tips="Keep it fast. Show the device while talking. Demonstrate the 15-second result."
              />

              <ScriptCard 
                title="The Close (ROI Pitch)"
                script="Here's the math: A single HAI claim can cost $50,000 or more. A scope repair from debris damage runs $15,000 to $50,000. CleanRead catches the failures BEFORE they become problems. One save pays for the system. And you get audit-ready documentation for every test, protecting your facility and your patients."
                tips="Always end with patient safety, then cost savings. CFOs respond to both."
              />
            </div>

            {/* Technical Specifications */}
            <div className="bg-slate-100 rounded-xl p-6 border border-slate-200">
              <h4 className="font-bold text-slate-800 mb-4">Technical Specifications</h4>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <p className="text-xs text-slate-500 uppercase mb-1">Item Number</p>
                  <p className="text-lg font-bold text-slate-800">CR001</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <p className="text-xs text-slate-500 uppercase mb-1">Detection Method</p>
                  <p className="text-lg font-bold text-slate-800">ATP Bioluminescence</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <p className="text-xs text-slate-500 uppercase mb-1">Result Time</p>
                  <p className="text-lg font-bold text-slate-800">15 Seconds</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <p className="text-xs text-slate-500 uppercase mb-1">Connectivity</p>
                  <p className="text-lg font-bold text-slate-800">Wi-Fi + Offline</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-200">
                <h5 className="font-semibold text-slate-700 mb-2">Screen Features:</h5>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm">
                  <p className="text-slate-600">• Today's Plan view</p>
                  <p className="text-slate-600">• Test Points library</p>
                  <p className="text-slate-600">• Quick Test mode</p>
                  <p className="text-slate-600">• Reports access</p>
                  <p className="text-slate-600">• Barcode scanner</p>
                  <p className="text-slate-600">• Cloud sync status</p>
                </div>
              </div>
            </div>

            {/* Objection Handlers */}
            <div className="space-y-3">
              <h4 className="font-semibold text-slate-800">🛡️ Objection Handlers</h4>
              <ObjectionHandler 
                objection="We already do visual inspection"
                response="Visual inspection is important—but it can't detect ATP. Blood residue, biofilm, bacteria—they're all invisible to the naked eye. CleanRead gives you objective, documented proof that cleaning was effective. It's the difference between 'it looks clean' and 'here's the RLU reading proving it's clean.'"
              />
              <ObjectionHandler 
                objection="We don't have time for another step"
                response="15 seconds. That's all it takes. And you can test while the scope is still at the sink—before it even moves to the next station. The time you save by catching failures early—before reprocessing, before patient use—far outweighs 15 seconds. Plus, you're creating automatic compliance documentation."
              />
              <ObjectionHandler 
                objection="Our staff is already stretched thin"
                response="That's exactly why CleanRead is designed for simplicity. 5-inch touchscreen works with gloves. RFID/barcode scanning eliminates manual data entry. Results auto-sync to cloud. Your staff gets a tool that makes their job easier while creating the documentation you need."
              />
              <ObjectionHandler 
                objection="How is this different from other ATP systems?"
                response="Three things: First, the smart handheld with offline mode means you can test anywhere—no cart, no station dependence. Second, the cloud portal with customizable RLU thresholds lets you set standards per instrument type. Third, AI-based test scheduling ensures consistent protocols. It's not just a reader—it's a complete contamination monitoring system."
              />
            </div>

            {/* Ordering Info */}
            <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
              <h4 className="font-bold text-emerald-800 mb-4 flex items-center gap-2">
                <Phone className="w-5 h-5" />
                Ordering & Support
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 border border-emerald-200">
                  <p className="font-semibold text-slate-800 mb-2">Ruhof Sales</p>
                  <p className="text-slate-600">1-800-537-8463</p>
                  <a href="https://www.ruhof.com" className="text-emerald-600 hover:underline text-sm">www.ruhof.com</a>
                </div>
                <div className="bg-white rounded-lg p-4 border border-emerald-200">
                  <p className="font-semibold text-slate-800 mb-2">Empire Bio Diagnostics</p>
                  <p className="text-slate-600">orders@ebdny.com</p>
                  <p className="text-sm text-slate-500">280 Duffy Avenue, Hicksville, NY 11801</p>
                  <p className="text-sm text-slate-500">Local: (516) 696-0822</p>
                </div>
              </div>
            </div>

          </div>
        </Section>

        {/* Healthcare Water Purification System - In-Person Presentation */}
        <Section title="💧 Healthcare Water Purification Systems Demo" icon={Droplets} color="blue">
          <div className="space-y-8">
            {/* Hero with Achievement Banner */}
            <div className="relative overflow-hidden rounded-2xl">
              <img 
                src="https://sterilemate.com/wp-content/uploads/2023/01/hospital-water-treatment-02.jpg" 
                alt="Healthcare Water Purification System" 
                className="w-full h-72 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-900/95 via-cyan-800/80 to-transparent flex items-center">
                <div className="p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center border border-white/30">
                      <Droplets className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">Healthcare Water Solutions</h3>
                      <p className="text-cyan-200">AAMI ST108 Compliant Water Purification</p>
                    </div>
                  </div>
                  <div className="bg-emerald-500/30 backdrop-blur rounded-xl p-4 border border-emerald-400/50 inline-block mb-4">
                    <p className="text-white font-bold text-lg flex items-center gap-2">
                      <Award className="w-5 h-5 text-emerald-300" />
                      14 Large-Scale Facility-Wide Systems Built in Texas Since 2023
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <a 
                      href="https://sterilemate.com/healthcare-water-purification-education/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white text-blue-700 rounded-lg hover:bg-blue-50 transition-colors font-medium"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Education Portal
                    </a>
                    <a 
                      href="https://customer-assets.emergentagent.com/job_supply-backend/artifacts/iknj4per_Sterile_Mate_Sai_Water_Healthcare_Water_Purification_Systems_2025.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-blue-700 text-white border border-blue-500 rounded-lg hover:bg-blue-600 transition-colors font-medium"
                      data-testid="water-purification-brochure-btn"
                    >
                      <FileText className="w-4 h-4" />
                      Water Purification Brochure
                    </a>
                    <PDFDownloadLink 
                      document={<ProductDatasheetPDF />} 
                      fileName="Sterile_Mate_ROS-IXC-850T_Datasheet.pdf"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium"
                      data-testid="ro-system-datasheet-btn"
                    >
                      {({ loading }) => (
                        loading ? (
                          <>
                            <span className="animate-spin text-sm">⏳</span>
                            Generating...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4" />
                            RO System Datasheet
                          </>
                        )
                      )}
                    </PDFDownloadLink>
                  </div>
                </div>
              </div>
            </div>

            {/* Texas Track Record */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-xl p-6 text-white">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <MapPin className="w-7 h-7" />
                </div>
                <div>
                  <h4 className="text-xl font-bold">Texas Healthcare Track Record</h4>
                  <p className="text-amber-100">Proven Performance in Critical Water Systems</p>
                </div>
              </div>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
                  <p className="text-4xl font-bold">14</p>
                  <p className="text-amber-100 text-sm">Large-Scale Systems</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
                  <p className="text-4xl font-bold">40+</p>
                  <p className="text-amber-100 text-sm">Years Experience</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
                  <p className="text-4xl font-bold">400+</p>
                  <p className="text-amber-100 text-sm">RO Training Hours</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
                  <p className="text-4xl font-bold">24/7</p>
                  <p className="text-amber-100 text-sm">Emergency Support</p>
                </div>
              </div>
            </div>

            {/* Opening Hook */}
            <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Play className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-2">🎬 Opening Hook for In-Person Presentation</h4>
                  <p className="text-white/90 text-lg italic">
                    "Your sterile processing is only as good as your water. If you're not meeting AAMI ST108, every instrument you process 
                    is at risk—pitting, corrosion, biofilm, mineral scaling. We've built 14 facility-wide water purification systems 
                    for Texas healthcare facilities since 2023. Let me show you what compliant water looks like."
                  </p>
                </div>
              </div>
            </div>

            {/* System Images Gallery */}
            <div className="space-y-4">
              <h4 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Factory className="w-5 h-5 text-blue-600" />
                Our Systems in Action (Use for Visual Presentation)
              </h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="rounded-xl overflow-hidden border border-slate-200 shadow-lg">
                  <img 
                    src="https://sterilemate.com/wp-content/uploads/2023/01/hospital-water-treatment-01.jpg" 
                    alt="Complete Water Purification System" 
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-3 bg-white">
                    <p className="text-sm font-medium text-slate-700">RO + EDI System Integration</p>
                    <p className="text-xs text-slate-500">Multi-stage filtration for critical water</p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden border border-slate-200 shadow-lg">
                  <img 
                    src="https://sterilemate.com/wp-content/uploads/2023/01/industrial-water-treament.jpg" 
                    alt="Industrial Scale Filtration" 
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-3 bg-white">
                    <p className="text-sm font-medium text-slate-700">Industrial Scale Filtration</p>
                    <p className="text-xs text-slate-500">Facility-wide distribution</p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden border border-slate-200 shadow-lg">
                  <img 
                    src="https://sterilemate.com/wp-content/uploads/2023/01/water-treatment-equipment-01.jpg" 
                    alt="Installation Services" 
                    className="w-full h-48 object-cover"
                  />
                  <div className="p-3 bg-white">
                    <p className="text-sm font-medium text-slate-700">Turnkey Installation</p>
                    <p className="text-xs text-slate-500">Design to commissioning</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Why AAMI ST108 Matters */}
            <div className="bg-red-50 rounded-xl p-6 border border-red-200">
              <h4 className="text-lg font-bold text-red-800 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                The Problem: Why Water Quality Matters in SPD
              </h4>
              <p className="text-slate-700 mb-4 italic">
                "AAMI ST108 isn't optional—it's the standard for medical device reprocessing water. Non-compliant water causes..."
              </p>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 border border-red-200 text-center">
                  <p className="font-bold text-red-700 text-lg mb-1">Pitting & Corrosion</p>
                  <p className="text-sm text-slate-600">Instrument damage from mineral deposits</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-red-200 text-center">
                  <p className="font-bold text-red-700 text-lg mb-1">Biofilm Buildup</p>
                  <p className="text-sm text-slate-600">Microbial contamination in lines</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-red-200 text-center">
                  <p className="font-bold text-red-700 text-lg mb-1">Mineral Scaling</p>
                  <p className="text-sm text-slate-600">Equipment failure and clogging</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-red-200 text-center">
                  <p className="font-bold text-red-700 text-lg mb-1">Stagnant Water</p>
                  <p className="text-sm text-slate-600">Bacterial growth at outlet ports</p>
                </div>
              </div>
            </div>

            {/* Water Purification Technologies */}
            <div className="space-y-4">
              <h4 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Settings className="w-5 h-5 text-blue-600" />
                Our Water Purification Technologies (Demo Talking Points)
              </h4>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Droplets className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h5 className="font-bold text-slate-800">Reverse Osmosis (RO)</h5>
                      <p className="text-sm text-blue-600">Up to 99.4% Purification</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />Removes suspended and dissolved solids</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />Up to 80% recovery rates (minimal waste)</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />2,100-15,600 GPD production range</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />Ships pre-assembled for fast install</li>
                  </ul>
                </div>
                <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                      <Zap className="w-6 h-6 text-cyan-600" />
                    </div>
                    <div>
                      <h5 className="font-bold text-slate-800">Electrodeionization (EDI)</h5>
                      <p className="text-sm text-cyan-600">Ultimate Purity</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />Removes residual salts & dissolved solids</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />Captures CO2, silica, ammonia, boron</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />Pairs with RO for complete purification</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />Ideal for sterilization & treatments</li>
                  </ul>
                </div>
                <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center">
                      <Shield className="w-6 h-6 text-violet-600" />
                    </div>
                    <div>
                      <h5 className="font-bold text-slate-800">UV Disinfection</h5>
                      <p className="text-sm text-violet-600">99.9% Bacteria Removal</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />254nm UV light destroys DNA</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />Prevents bacteria reproduction</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />Chemical-free, odor-free, taste-free</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />Meets highest medical standards</li>
                  </ul>
                </div>
                <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-lg">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Gauge className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h5 className="font-bold text-slate-800">Water Softening & Filtration</h5>
                      <p className="text-sm text-amber-600">Pre-Treatment Excellence</p>
                    </div>
                  </div>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />Removes calcium & magnesium (scale)</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />Carbon filters remove chlorine/chloramines</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />Protects RO membranes</li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />Extends equipment life</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Technical Specifications */}
            <div className="bg-slate-900 rounded-xl p-6 text-white">
              <h4 className="text-lg font-bold text-cyan-400 mb-4 flex items-center gap-2">
                <Layers className="w-5 h-5" />
                ROS-IXC Series Specifications
              </h4>
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                <div className="bg-slate-800 rounded-lg p-4 text-center border border-slate-700">
                  <p className="text-2xl font-bold text-white">99%</p>
                  <p className="text-cyan-300 text-sm">Rejection Rate</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4 text-center border border-slate-700">
                  <p className="text-2xl font-bold text-white">80%</p>
                  <p className="text-cyan-300 text-sm">Recovery Rate</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4 text-center border border-slate-700">
                  <p className="text-2xl font-bold text-white">5-25 GPM</p>
                  <p className="text-cyan-300 text-sm">Flow Rates</p>
                </div>
                <div className="bg-slate-800 rounded-lg p-4 text-center border border-slate-700">
                  <p className="text-2xl font-bold text-white">1000 Gal</p>
                  <p className="text-cyan-300 text-sm">Storage Tank</p>
                </div>
              </div>
              <div className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                <h5 className="font-semibold text-white mb-3">System Components:</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-slate-300">
                  <p>• UV Light (254nm)</p>
                  <p>• Endotoxin Filtration (0.2µm)</p>
                  <p>• PLC Control System</p>
                  <p>• Remote Monitoring</p>
                  <p>• Circulation Pump</p>
                  <p>• Multi-Media Filter</p>
                  <p>• 1000 Gal Storage</p>
                  <p>• Booster Pump</p>
                </div>
              </div>
            </div>

            {/* Applications */}
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
              <h4 className="font-bold text-blue-800 mb-4 flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Healthcare Applications (Demo Talking Points)
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { area: "Central Sterile (SPD)", icon: "🏥" },
                  { area: "Operating Rooms", icon: "⚕️" },
                  { area: "Dialysis Centers", icon: "💉" },
                  { area: "Compounding Pharmacy", icon: "💊" },
                  { area: "Endoscopy Suites", icon: "🔬" },
                  { area: "Research Labs", icon: "🧪" },
                  { area: "Cleanrooms", icon: "🧼" },
                  { area: "GI Labs", icon: "📋" }
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-lg p-3 text-center border border-blue-200">
                    <span className="text-2xl">{item.icon}</span>
                    <p className="text-sm font-medium text-slate-700 mt-1">{item.area}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Full-Service Capabilities */}
            <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
              <h4 className="font-bold text-emerald-800 mb-4 flex items-center gap-2">
                <Wrench className="w-5 h-5" />
                Full-Service General Contracting (Key Differentiator)
              </h4>
              <p className="text-slate-700 mb-4 italic">
                "We're not just selling equipment—we're your turnkey partner from design to commissioning."
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                {[
                  { title: "Design Engineering", items: ["CAD drawings", "System optimization", "AAMI ST108 compliance design"] },
                  { title: "Construction", items: ["Room buildouts", "Plumbing (PVC/PEX/Copper)", "Electrical pre-installation"] },
                  { title: "Commissioning", items: ["Equipment placement", "Controls programming", "Training & certification"] }
                ].map((phase, i) => (
                  <div key={i} className="bg-white rounded-lg p-4 border border-emerald-200">
                    <h5 className="font-semibold text-emerald-700 mb-2">{phase.title}</h5>
                    <ul className="space-y-1 text-sm text-slate-600">
                      {phase.items.map((item, j) => (
                        <li key={j} className="flex items-center gap-2">
                          <CircleCheck className="w-3 h-3 text-emerald-500" />{item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Sales Scripts */}
            <div className="space-y-4">
              <h4 className="text-lg font-bold text-slate-800">📝 Presentation Scripts</h4>
              
              <ScriptCard 
                title="Opening (Discovery)"
                script="When was the last time you tested your water quality against AAMI ST108? Do you know what your current hardness levels are? Because if you're above 3 GPG, you're creating conditions for scale buildup that damages every instrument in your SPD."
                tips="Lead with their compliance gap. Pull up the Water Hardness Scale visual."
              />
              
              <ScriptCard 
                title="The Solution Pitch"
                script="We've built 14 facility-wide water purification systems for Texas healthcare facilities since 2023. Our systems combine reverse osmosis with UV disinfection and EDI to deliver AAMI ST108 compliant water—99.4% purification with up to 80% water recovery. And we don't just sell equipment. We design, install, and commission the entire system. Turnkey."
                tips="Emphasize the Texas track record and turnkey approach. Show the system images."
              />

              <ScriptCard 
                title="ROI Close"
                script="Every time you reprocess an instrument with non-compliant water, you're shortening its lifespan. Scope repairs run $15,000 to $50,000. A single biofilm contamination event can shut down your SPD. Our systems pay for themselves by protecting your instruments and ensuring compliance. Plus, 24/7 emergency support means we're there when you need us."
                tips="Connect to instrument protection cost, then patient safety."
              />
            </div>

            {/* AmeriWater Dialysis Water Purification Systems */}
            <div className="bg-gradient-to-br from-slate-900 via-blue-950 to-cyan-950 rounded-2xl p-6 text-white">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-cyan-500/20 rounded-xl flex items-center justify-center border border-cyan-400/50">
                  <Activity className="w-7 h-7 text-cyan-400" />
                </div>
                <div>
                  <h4 className="text-xl font-bold">Dialysis Water Purification — AmeriWater Systems</h4>
                  <p className="text-cyan-300 text-sm">FDA 510K Registered | ISO 13485 | ANSI/AAMI Compliant</p>
                </div>
                <div className="ml-auto bg-emerald-500/20 border border-emerald-400/50 rounded-lg px-4 py-2">
                  <p className="text-emerald-300 text-sm font-bold">510K Certified</p>
                  <p className="text-emerald-400/70 text-xs">Medical Device</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mb-6">
                {/* Option 1: Direct Feed */}
                <div className="bg-white/5 rounded-xl p-5 border border-cyan-500/30" data-testid="playbook-direct-feed">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center">
                      <Thermometer className="w-5 h-5 text-orange-400" />
                    </div>
                    <h5 className="font-bold text-cyan-300">Option 1: Direct Feed + Hot Water Disinfection</h5>
                  </div>
                  <p className="text-slate-300 text-sm mb-3 italic">&quot;MediQA Heat Disinfect RO — the workhorse for dialysis centers needing maximum uptime and automated disinfection.&quot;</p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-slate-800/80 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-white">23,040</p>
                      <p className="text-cyan-300 text-xs">GPD Capacity</p>
                    </div>
                    <div className="bg-slate-800/80 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-white">40%</p>
                      <p className="text-cyan-300 text-xs">Less Water Waste</p>
                    </div>
                    <div className="bg-slate-800/80 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-white">&gt;94%</p>
                      <p className="text-cyan-300 text-xs">Salt Rejection</p>
                    </div>
                    <div className="bg-slate-800/80 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-white">5</p>
                      <p className="text-cyan-300 text-xs">Model Options</p>
                    </div>
                  </div>
                  <ul className="space-y-1.5 text-sm text-cyan-200">
                    <li className="flex items-start gap-2"><CircleCheck className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />Single &amp; dual pass configurations available</li>
                    <li className="flex items-start gap-2"><CircleCheck className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />8&quot; TFC heat disinfectable membranes in FRP vessels</li>
                    <li className="flex items-start gap-2"><CircleCheck className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />High Recovery Mode — 100% recirculation</li>
                    <li className="flex items-start gap-2"><CircleCheck className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />Emergency Single Pass Mode (ESPM) for uninterrupted service</li>
                    <li className="flex items-start gap-2"><CircleCheck className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />Full color touchscreen + advanced data logging (Excel export)</li>
                    <li className="flex items-start gap-2"><CircleCheck className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />Powder coated aluminum skid on castors</li>
                  </ul>
                  <a href="https://customer-assets.emergentagent.com/job_679aa064-a1c3-435f-9d63-e85386ec1c5c/artifacts/jukvtufy_MediQA_Heat_Disinfect_RO.pdf"
                    target="_blank" rel="noopener noreferrer" data-testid="playbook-mediqa-download"
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg text-white text-sm font-medium transition-colors">
                    <Download className="w-4 h-4" /> Download MediQA Spec Sheet
                  </a>
                </div>

                {/* Option 2: Reserve Tank */}
                <div className="bg-white/5 rounded-xl p-5 border border-blue-500/30" data-testid="playbook-reserve-tank">
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center">
                      <Server className="w-5 h-5 text-blue-400" />
                    </div>
                    <h5 className="font-bold text-blue-300">Option 2: Reserve Tank with Heat Disinfection</h5>
                  </div>
                  <p className="text-slate-300 text-sm mb-3 italic">&quot;Heatsan — automated loop disinfection with 100-gallon insulated reserve for reliable hot water supply to every station.&quot;</p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <div className="bg-slate-800/80 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-white">100</p>
                      <p className="text-blue-300 text-xs">Gal Reserve Tank</p>
                    </div>
                    <div className="bg-slate-800/80 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-white">185F</p>
                      <p className="text-blue-300 text-xs">Hot Water Supply</p>
                    </div>
                    <div className="bg-slate-800/80 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-white">2hr</p>
                      <p className="text-blue-300 text-xs">Hold for Bacteria Kill</p>
                    </div>
                    <div className="bg-slate-800/80 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-white">12mo</p>
                      <p className="text-blue-300 text-xs">Data Event Log</p>
                    </div>
                  </div>
                  <ul className="space-y-1.5 text-sm text-blue-200">
                    <li className="flex items-start gap-2"><CircleCheck className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />Automated weekly heat disinfection cycle</li>
                    <li className="flex items-start gap-2"><CircleCheck className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />Intelligent monitoring with leak detection &amp; auto-abort</li>
                    <li className="flex items-start gap-2"><CircleCheck className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />Stainless steel skid, tank, pump &amp; solenoid valves</li>
                    <li className="flex items-start gap-2"><CircleCheck className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />PLC controller with dual-color touch screen</li>
                    <li className="flex items-start gap-2"><CircleCheck className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />7-day programmable cycle syncs with dialysis machines</li>
                    <li className="flex items-start gap-2"><CircleCheck className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />4 levels of password protection</li>
                  </ul>
                  <a href="https://customer-assets.emergentagent.com/job_679aa064-a1c3-435f-9d63-e85386ec1c5c/artifacts/ep4sumnq_Heatsan_Heat_Disinfection_System.pdf"
                    target="_blank" rel="noopener noreferrer" data-testid="playbook-heatsan-download"
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-white text-sm font-medium transition-colors">
                    <Download className="w-4 h-4" /> Download Heatsan Spec Sheet
                  </a>
                </div>
              </div>

              {/* Post-Treatment Rack */}
              <div className="bg-white/5 rounded-xl p-5 border border-violet-500/30 mb-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Server className="w-5 h-5 text-violet-400" />
                  </div>
                  <div className="flex-1">
                    <h5 className="font-bold text-violet-300 mb-1">Post-Treatment Rack — CPVC Heat Rated</h5>
                    <p className="text-slate-300 text-sm mb-3">Multi-function post-treatment device combining DI Bypass header, endotoxin filtration, and alarm panel in one unit. Reduces installation time and provides easy access for service.</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                      <div className="bg-slate-800/80 rounded p-2 text-center text-xs">
                        <p className="text-violet-300">DI Bypass</p>
                        <p className="text-white font-semibold">Emergency Ready</p>
                      </div>
                      <div className="bg-slate-800/80 rounded p-2 text-center text-xs">
                        <p className="text-violet-300">Endotoxin Filter</p>
                        <p className="text-white font-semibold">0.2 um / 20&quot;</p>
                      </div>
                      <div className="bg-slate-800/80 rounded p-2 text-center text-xs">
                        <p className="text-violet-300">Temp Rating</p>
                        <p className="text-white font-semibold">200F</p>
                      </div>
                      <div className="bg-slate-800/80 rounded p-2 text-center text-xs">
                        <p className="text-violet-300">Alarm Panel</p>
                        <p className="text-white font-semibold">Nurse Station</p>
                      </div>
                    </div>
                    <a href="https://customer-assets.emergentagent.com/job_679aa064-a1c3-435f-9d63-e85386ec1c5c/artifacts/76c4dha4_D-357-01-Post-Treatment-Rack.pdf"
                      target="_blank" rel="noopener noreferrer" data-testid="playbook-post-treatment-download"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-500 rounded-lg text-white text-sm font-medium transition-colors">
                      <Download className="w-4 h-4" /> Download Post-Treatment Rack Spec Sheet
                    </a>
                  </div>
                </div>
              </div>

              {/* Turnkey Script */}
              <div className="bg-cyan-600/20 rounded-xl p-5 border border-cyan-500/30">
                <h5 className="font-bold text-cyan-300 mb-2 flex items-center gap-2">
                  <Wrench className="w-5 h-5" />
                  Turnkey Service Talking Point
                </h5>
                <p className="text-white text-sm italic mb-3">
                  &quot;We don&apos;t just sell the equipment — we deliver the complete project. Room buildout, electrical modifications, plumbing, distribution loop installation, equipment commissioning, and staff training. One contractor, one point of contact, from design through turnover. Every AmeriWater system we install is FDA 510K registered with full ANSI/AAMI compliance.&quot;
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="bg-white/10 rounded-lg p-2 text-center text-xs">
                    <p className="text-cyan-300 font-semibold">Room Buildout</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-2 text-center text-xs">
                    <p className="text-cyan-300 font-semibold">Electrical</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-2 text-center text-xs">
                    <p className="text-cyan-300 font-semibold">Plumbing</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-2 text-center text-xs">
                    <p className="text-cyan-300 font-semibold">Commissioning</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Certifications */}
            <div className="bg-slate-100 rounded-xl p-6 border border-slate-200">
              <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                <BadgeCheck className="w-5 h-5 text-blue-600" />
                Certifications & Compliance
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 text-center border border-slate-200">
                  <p className="font-bold text-blue-700">AAMI ST108</p>
                  <p className="text-xs text-slate-500">Medical Device Reprocessing</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center border border-slate-200">
                  <p className="font-bold text-blue-700">NSF / WQA / FDA</p>
                  <p className="text-xs text-slate-500">Product Approvals</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center border border-slate-200">
                  <p className="font-bold text-blue-700">HUB Certified</p>
                  <p className="text-xs text-slate-500">Texas Underutilized Business</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center border border-slate-200">
                  <p className="font-bold text-blue-700">MBE Certified</p>
                  <p className="text-xs text-slate-500">Minority Business Enterprise</p>
                </div>
              </div>
            </div>

            {/* Objection Handlers */}
            <div className="space-y-3">
              <h4 className="font-semibold text-slate-800">🛡️ Objection Handlers</h4>
              <ObjectionHandler 
                objection="We already have a water system"
                response="Great—how old is it? When was the last water quality test? Most systems we replace are outdated and no longer meet AAMI ST108. We can do a complimentary water analysis to show you exactly where you stand. Often facilities discover they're out of compliance without knowing it."
              />
              <ObjectionHandler 
                objection="This seems expensive"
                response="I understand. But consider this: every instrument processed with non-compliant water degrades faster. A single scope repair is $15,000-$50,000. Our systems pay for themselves by extending equipment life. Plus, we offer flexible financing and service contracts to fit your budget."
              />
              <ObjectionHandler 
                objection="We don't have space for this"
                response="Our systems are designed for healthcare facilities—compact footprint, vertical configurations, and modular designs. We've fit complete systems into tight spaces across 14 Texas facilities. Let us do a site survey—you might be surprised."
              />
            </div>
          </div>
        </Section>

        {/* Water Sampling & Microbiology Testing Demo */}
        <Section title="🔬 Water Sampling & Microbiology Testing Demo" icon={FlaskConical} color="green">
          <div className="space-y-8">
            {/* Hero Banner */}
            <div className="relative overflow-hidden rounded-2xl">
              <img 
                src="https://sterilemate.com/wp-content/uploads/2025/11/holding-up-a-petri-dish-with-bacteria.webp" 
                alt="Microbiology Testing" 
                className="w-full h-72 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/95 via-teal-800/80 to-transparent flex items-center">
                <div className="p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center border border-white/30">
                      <Microscope className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">Microbiology Testing & Water Sampling</h3>
                      <p className="text-emerald-200">ISO/IEC 17025:2017 Accredited Laboratory</p>
                    </div>
                  </div>
                  <p className="text-white/90 text-lg max-w-xl mb-4">
                    Comprehensive microbiological analysis with AAMI ST108 compliance for healthcare, industrial, and municipal water testing.
                  </p>
                  <div className="flex gap-3">
                    <a 
                      href="https://sterilemate.com/microbiology-testing/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors font-medium"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Testing Services
                    </a>
                    <a 
                      href="https://customer-assets.emergentagent.com/job_c4eb42da-4586-4303-b844-90350e5cf110/artifacts/dkj8jkmu_Sterile_Mate_Sai_Water_Healthcare_Water_Sampling%20Services.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-700 text-white border border-emerald-500 rounded-lg hover:bg-emerald-600 transition-colors font-medium"
                    >
                      <FileText className="w-4 h-4" />
                      Services Brochure
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Lab Accreditations */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
              <h4 className="text-lg font-bold mb-4 flex items-center gap-2">
                <Award className="w-5 h-5" />
                Lab Accreditations & Certifications (Trust Builders)
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
                  <p className="font-bold">ISO/IEC 17025:2017</p>
                  <p className="text-emerald-200 text-xs">International Standard</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
                  <p className="font-bold">TCEQ Approved</p>
                  <p className="text-emerald-200 text-xs">Drinking Water Lab</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
                  <p className="font-bold">NELAP Accredited</p>
                  <p className="text-emerald-200 text-xs">Environmental Testing</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
                  <p className="font-bold">CDC Legionella Elite</p>
                  <p className="text-emerald-200 text-xs">Specialized Program</p>
                </div>
              </div>
            </div>

            {/* Opening Hook */}
            <div className="bg-gradient-to-r from-teal-600 to-cyan-600 rounded-xl p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Play className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-2">🎬 Opening Hook for Water Sampling Demo</h4>
                  <p className="text-white/90 text-lg italic">
                    "You already know you need to test—it's mandated. The question is: who's your partner? We're local, 
                    right here in Texas. ISO-accredited for accuracy. Sample pickup in Houston, Baton Rouge, and Tempe. 
                    Results in 5-7 days—rush available down to 24 hours. Let me show you why facilities across the Gulf States 
                    trust us with their compliance testing."
                  </p>
                </div>
              </div>
            </div>

            {/* Value Proposition Banner */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-6 text-white">
              <h4 className="text-lg font-bold mb-4 text-center">Why Partner With Sterile Mate for Your Mandated Testing?</h4>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Zap className="w-6 h-6" />
                  </div>
                  <p className="font-bold text-lg">Efficient</p>
                  <p className="text-blue-200 text-sm">5-7 day standard TAT, rush to 24hrs</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <Truck className="w-6 h-6" />
                  </div>
                  <p className="font-bold text-lg">Convenient</p>
                  <p className="text-blue-200 text-sm">Sample kits shipped free, courier pickup</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <p className="font-bold text-lg">Local</p>
                  <p className="text-blue-200 text-sm">Texas-based, Gulf States coverage</p>
                </div>
                <div className="bg-white/10 backdrop-blur rounded-lg p-4 text-center">
                  <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-2">
                    <BadgeCheck className="w-6 h-6" />
                  </div>
                  <p className="font-bold text-lg">Accurate</p>
                  <p className="text-blue-200 text-sm">ISO 17025, audit-ready reports</p>
                </div>
              </div>
            </div>

            {/* Testing Categories */}
            <div className="space-y-4">
              <h4 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <FlaskConical className="w-5 h-5 text-emerald-600" />
                Microbiology Testing Services (Demo Menu)
              </h4>
              <div className="grid md:grid-cols-4 gap-4">
                {[
                  { 
                    icon: "https://sterilemate.com/wp-content/uploads/2025/11/bacteria-icon-01.webp",
                    title: "Culture ID",
                    desc: "Genus-level identification"
                  },
                  { 
                    icon: "https://sterilemate.com/wp-content/uploads/2025/11/bacteria-icon-02.webp",
                    title: "Yeast & Mold",
                    desc: "Identification & screens"
                  },
                  { 
                    icon: "https://sterilemate.com/wp-content/uploads/2025/11/water-drop-icon-01.webp",
                    title: "Total Coliforms",
                    desc: "E.coli detection"
                  },
                  { 
                    icon: "https://sterilemate.com/wp-content/uploads/2025/11/test-01.webp",
                    title: "Legionella",
                    desc: "CDC Elite Program"
                  },
                  { 
                    icon: "https://sterilemate.com/wp-content/uploads/2025/11/magnifying-glass-01.webp",
                    title: "HPC",
                    desc: "Heterotrophic Plate Count"
                  },
                  { 
                    icon: "https://sterilemate.com/wp-content/uploads/2025/11/test-tube-01.webp",
                    title: "Pseudomonas",
                    desc: "Aeruginosa detection"
                  },
                  { 
                    icon: "https://sterilemate.com/wp-content/uploads/2025/11/molecule-01.webp",
                    title: "Iron/Sulfate",
                    desc: "Reducing bacteria"
                  },
                  { 
                    icon: "https://sterilemate.com/wp-content/uploads/2025/11/petri-dish-01.webp",
                    title: "Culturable",
                    desc: "Full analysis"
                  }
                ].map((test, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 border border-slate-200 hover:shadow-lg transition-shadow">
                    <img src={test.icon} alt={test.title} className="w-12 h-12 mx-auto mb-3" />
                    <h5 className="font-bold text-slate-800 text-center">{test.title}</h5>
                    <p className="text-xs text-slate-500 text-center">{test.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* AAMI ST108 Testing Panel */}
            <div className="bg-blue-900 rounded-xl p-6 text-white">
              <h4 className="text-lg font-bold text-cyan-400 mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5" />
                AAMI ST108 Water Quality Panel (Healthcare Focus)
              </h4>
              <p className="text-blue-200 mb-4 italic">"This is the panel you need for medical device reprocessing compliance."</p>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { test: "pH / Corrosivity", method: "SW-846 9041A", tat: "5 Days" },
                  { test: "Conductivity", method: "SM 2510B", tat: "5 Days" },
                  { test: "Total Hardness", method: "SM 2340C", tat: "5 Days" },
                  { test: "TOC", method: "SW-846 9060A", tat: "5 Days" },
                  { test: "HPC @ 35°C", method: "SM 9215B", tat: "5 Days" },
                  { test: "Chloride", method: "EPA 300.0", tat: "5 Days" },
                  { test: "Fe, Cu, Mn", method: "EPA 200.7", tat: "5 Days" },
                  { test: "Endotoxins", method: "Specialized", tat: "5 Days" }
                ].map((item, i) => (
                  <div key={i} className="bg-blue-800 rounded-lg p-3 border border-blue-700">
                    <p className="font-bold text-white">{item.test}</p>
                    <p className="text-xs text-blue-300">{item.method}</p>
                    <p className="text-xs text-cyan-400 mt-1">TAT: {item.tat}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Complete Test Menu */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Clipboard className="w-5 h-5 text-emerald-600" />
                Complete Microbiology Test Menu
              </h4>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div>
                  <h5 className="font-semibold text-emerald-700 mb-2">Coliform & Indicators</h5>
                  <ul className="space-y-1 text-slate-600">
                    <li>• Total Coliform (Enzyme/MF)</li>
                    <li>• E.coli (Enzyme/MF/MT)</li>
                    <li>• Fecal Coliform (MF/MT)</li>
                    <li>• Enterococcus (Enzyme/MF)</li>
                    <li>• Fecal Streptococcus</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-emerald-700 mb-2">Pathogens</h5>
                  <ul className="space-y-1 text-slate-600">
                    <li>• Legionella (CDC/PCR)</li>
                    <li>• Pseudomonas aeruginosa</li>
                    <li>• Staphylococcus aureus</li>
                    <li>• Salmonella spp.</li>
                    <li>• Vibrio species</li>
                  </ul>
                </div>
                <div>
                  <h5 className="font-semibold text-emerald-700 mb-2">Specialized</h5>
                  <ul className="space-y-1 text-slate-600">
                    <li>• MIC (Corrosion bacteria)</li>
                    <li>• Iron/Sulfate reducing</li>
                    <li>• Slime-forming bacteria</li>
                    <li>• Nitrifying bacteria</li>
                    <li>• MSQPCR (Mold)</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Turnaround Times */}
            <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
              <h4 className="font-bold text-amber-800 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Turnaround Times & Rush Options
              </h4>
              <div className="grid md:grid-cols-5 gap-3">
                <div className="bg-white rounded-lg p-4 text-center border border-amber-200">
                  <p className="text-lg font-bold text-slate-800">5-7 Days</p>
                  <p className="text-xs text-amber-600 font-medium">STANDARD</p>
                  <p className="text-xs text-slate-500">No surcharge</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center border border-amber-200">
                  <p className="text-lg font-bold text-slate-800">4 Days</p>
                  <p className="text-xs text-amber-600 font-medium">RUSH</p>
                  <p className="text-xs text-slate-500">+25% surcharge</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center border border-amber-200">
                  <p className="text-lg font-bold text-slate-800">3 Days</p>
                  <p className="text-xs text-amber-600 font-medium">RUSH</p>
                  <p className="text-xs text-slate-500">+50% surcharge</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center border border-amber-200">
                  <p className="text-lg font-bold text-slate-800">2 Days</p>
                  <p className="text-xs text-amber-600 font-medium">URGENT</p>
                  <p className="text-xs text-slate-500">+75% surcharge</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center border border-red-200 bg-red-50">
                  <p className="text-lg font-bold text-red-700">1 Day</p>
                  <p className="text-xs text-red-600 font-medium">EMERGENCY</p>
                  <p className="text-xs text-slate-500">+100% surcharge</p>
                </div>
              </div>
              <p className="text-sm text-amber-700 mt-4 italic">
                Samples received before 3:00 PM processed same day. Results by 6:00 PM on due date.
              </p>
            </div>

            {/* Service Workflow */}
            <div className="bg-slate-900 rounded-xl p-6 text-white">
              <h4 className="text-lg font-bold text-emerald-400 mb-6 flex items-center gap-2">
                <Play className="w-5 h-5" />
                🎯 Water Sampling Workflow (Demo Script)
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  { step: 1, title: "Request Sample Kits", desc: "Order online or call 800.988.3892. Kits shipped free with 5-day notice.", tip: "Emphasize convenience—we come to them" },
                  { step: 2, title: "Collect Samples", desc: "Use provided containers with correct preservatives (TOC, HPC, Metals specific).", tip: "Show the different bottle types if available" },
                  { step: 3, title: "Submit Chain of Custody", desc: "Complete COC form (downloadable). Include test requests.", tip: "Stress documentation for compliance" },
                  { step: 4, title: "Courier Pickup", desc: "Schedule pickup in Houston, Baton Rouge, or Tempe. Or ship with prepaid label.", tip: "We serve Texas and beyond" },
                  { step: 5, title: "Lab Analysis", desc: "ISO 17025 accredited analysis per AAMI ST108, EPA, and state standards.", tip: "Mention the accreditations again" },
                  { step: 6, title: "Receive Results", desc: "PDF + Excel reports by 6PM on due date. Monthly data reports available.", tip: "Digital, audit-ready documentation" }
                ].map((item) => (
                  <div key={item.step} className="bg-slate-800 rounded-lg p-4 border border-slate-700">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-emerald-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                        {item.step}
                      </div>
                      <div className="flex-1">
                        <h5 className="font-semibold text-white">{item.title}</h5>
                        <p className="text-sm text-slate-300 mb-2">{item.desc}</p>
                        <p className="text-xs text-emerald-400 italic">💡 {item.tip}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Water Hardness Scale Visual */}
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 rounded-xl p-6 text-white">
              <h4 className="text-lg font-bold mb-4 flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Water Hardness Scale (Use This Visual in Presentations)
              </h4>
              <p className="text-cyan-100 mb-4 italic">"AAMI ST108 recommends water below 3 GPG. Where does your facility stand?"</p>
              <div className="flex gap-2">
                <div className="flex-1 bg-emerald-500 rounded-lg p-3 text-center">
                  <p className="font-bold">0-3 GPG</p>
                  <p className="text-xs">Soft Water ✓</p>
                </div>
                <div className="flex-1 bg-yellow-500 rounded-lg p-3 text-center text-slate-900">
                  <p className="font-bold">4-6 GPG</p>
                  <p className="text-xs">Moderately Hard</p>
                </div>
                <div className="flex-1 bg-orange-500 rounded-lg p-3 text-center">
                  <p className="font-bold">7-10 GPG</p>
                  <p className="text-xs">Hard Water</p>
                </div>
                <div className="flex-1 bg-red-500 rounded-lg p-3 text-center">
                  <p className="font-bold">11-14 GPG</p>
                  <p className="text-xs">Very Hard</p>
                </div>
                <div className="flex-1 bg-red-700 rounded-lg p-3 text-center">
                  <p className="font-bold">15+ GPG</p>
                  <p className="text-xs">Extremely Hard</p>
                </div>
              </div>
            </div>

            {/* Sales Scripts */}
            <div className="space-y-4">
              <h4 className="text-lg font-bold text-slate-800">📝 Water Sampling Presentation Scripts</h4>
              
              <ScriptCard 
                title="Partnership Opening"
                script="You're already doing your mandated testing—that's not the question. The question is: are you getting the convenience and service you deserve? We're local, right here in North Houston. We pick up samples across Texas and Louisiana. Results in 5-7 days, rush available same-week. And our reports are audit-ready for Joint Commission. Who are you currently working with, and how's that relationship going?"
                tips="Acknowledge they're already compliant. Position as a better partner, not a new need."
              />
              
              <ScriptCard 
                title="Convenience & Efficiency Pitch"
                script="Here's what makes us different: We ship sample kits free with 5-day notice. Our couriers pick up in Houston, Baton Rouge, and Tempe—no shipping hassle. Results by 6 PM on your due date, every time. Need it faster? We do 24-hour rush. And our online portal means no more paper shuffling—everything's digital, accessible anytime. We make compliance easy."
                tips="Stack the convenience factors. Emphasize they'll spend less time on logistics."
              />

              <ScriptCard 
                title="Local Partnership Close"
                script="When you work with a national lab, you're a number. With us, you get a dedicated Project Manager who knows your facility. We're 30 minutes away, not three time zones. If something comes back out of range, we're on the phone same day to discuss next steps. That's the local advantage. Ready to see what a real partnership looks like?"
                tips="Differentiate from big national labs. Local = responsive = better service."
              />
            </div>

            {/* Contact & Ordering */}
            <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
              <h4 className="font-bold text-emerald-800 mb-4 flex items-center gap-2">
                <Phone className="w-5 h-5" />
                How to Get Started
              </h4>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-4 border border-emerald-200">
                  <p className="font-semibold text-slate-800 mb-2">Request Sample Kits</p>
                  <p className="text-sm text-slate-600 mb-2">Free with 5-day advance notice</p>
                  <a href="https://sterilemate.com/bottle-request-form/" className="text-emerald-600 hover:underline text-sm">Bottle Request Form →</a>
                </div>
                <div className="bg-white rounded-lg p-4 border border-emerald-200">
                  <p className="font-semibold text-slate-800 mb-2">Get a Price Quote</p>
                  <p className="text-sm text-slate-600 mb-2">Custom testing packages</p>
                  <a href="https://sterilemate.com/price-quote-form/" className="text-emerald-600 hover:underline text-sm">Price Quote Form →</a>
                </div>
                <div className="bg-white rounded-lg p-4 border border-emerald-200">
                  <p className="font-semibold text-slate-800 mb-2">Contact Lab</p>
                  <p className="text-slate-600">800.988.3892</p>
                  <p className="text-slate-600">info@sterilemate.com</p>
                  <p className="text-xs text-slate-400 mt-1">Houston, TX 77067</p>
                </div>
              </div>
            </div>

            {/* Objection Handlers */}
            <div className="space-y-3">
              <h4 className="font-semibold text-slate-800">🛡️ Objection Handlers</h4>
              <ObjectionHandler 
                objection="We already use another lab"
                response="Perfect—so you know what good service looks like. Quick question: are they local? Do they pick up samples, or do you have to ship? How fast do you get results? We're right here in Texas, we pick up in Houston, Baton Rouge, and Tempe, and results come by 6 PM on your due date. Let us run a parallel test—if we're not better, no hard feelings."
              />
              <ObjectionHandler 
                objection="We're happy with our current provider"
                response="That's great to hear. We're not here to replace something that's working. But things change—labs get acquired, service slips, prices go up. Keep us in mind as a backup option. Here's my card. When your current contract comes up for renewal, let's talk. We'd love the chance to earn your business."
              />
              <ObjectionHandler 
                objection="Your pricing is higher"
                response="Fair point. But let me ask: what's the cost of a delayed result? A missed compliance deadline? A re-test because of a sample handling error? We include free sample kits, courier pickup, and audit-ready reporting. When you factor in the service, we're often more cost-effective. Let me build you a custom quote so you can compare apples to apples."
              />
              <ObjectionHandler 
                objection="We need faster turnaround"
                response="We hear you. Our standard is 5-7 days, but we offer rush options: 4-day at 25% premium, 3-day at 50%, 2-day at 75%, and 24-hour at 100%. For routine compliance, 5-7 is industry standard. But if you have recurring urgent needs, we can build expedited turnaround into your service agreement at a negotiated rate."
              />
            </div>
          </div>
        </Section>

        {/* ============================================ */}
        {/* INSTITUTIONAL SOVEREIGNTY SECTION */}
        {/* ============================================ */}
        
        <div className="my-8 bg-gradient-to-r from-indigo-900 via-purple-900 to-indigo-900 rounded-xl p-6 text-white">
          <div className="text-center mb-2">
            <span className="bg-amber-500 text-black px-4 py-1 rounded-full text-sm font-bold">
              THE CLOSER
            </span>
          </div>
          <h2 className="text-2xl font-bold text-center">Institutional Sovereignty Suite</h2>
          <p className="text-center text-purple-200 mt-2">
            Transform from Vendor to Governance Infrastructure Asset
          </p>
          <div className="flex justify-center gap-4 mt-4">
            <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold">🟢 LIVE = Operational Now</span>
            <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-bold">🔵 Q1 2026 = In Development</span>
            <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-xs font-bold">🟣 ROADMAP = Planned</span>
          </div>
        </div>

        {/* Healthcare General Contracting */}
        <Section title="Healthcare General Contracting Services" icon={Building2} color="orange">
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6">
              <h4 className="text-xl font-bold text-amber-800 mb-2">Electrical. Plumbing. Construction.</h4>
              <p className="text-amber-700 mb-4">Licensed, experienced technicians with years of healthcare, industrial, commercial, and residential contracting expertise. Fully vetted for healthcare compliance — meeting all compliance measures for medical facilities.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Electrical', items: ['Panel Upgrades', 'Medical Equipment Wiring', 'Emergency Power', 'Fire Alarm Systems'] },
                  { label: 'Plumbing', items: ['Healthcare Water Systems', 'Gas Line Install/Repair', 'Leak Detection', 'Backflow Prevention'] },
                  { label: 'Construction', items: ['Room Buildouts', 'New Construction', 'Remodels', 'ADA Compliance'] },
                  { label: 'Repairs', items: ['Emergency Leaks', 'Gas Repairs', 'Electrical Troubleshooting', '24/7 Response'] },
                ].map((cat, i) => (
                  <div key={i} className="bg-white rounded-lg p-4 border border-amber-100">
                    <p className="font-bold text-amber-800 mb-2">{cat.label}</p>
                    {cat.items.map((item, j) => (
                      <p key={j} className="text-xs text-slate-600">- {item}</p>
                    ))}
                  </div>
                ))}
              </div>
              <div className="bg-amber-100 rounded-lg p-4 mb-4">
                <p className="text-sm font-bold text-amber-900 mb-1">Reference Clients:</p>
                <p className="text-sm text-amber-800">Harris County Healthcare System | State of Texas | VA Medical Centers</p>
                <p className="text-xs text-amber-700 mt-1">Capital projects, room buildouts, healthcare plumbing systems, electrical for capital projects. No job too big or too small.</p>
              </div>
              <div className="flex gap-3">
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/downloads/gc-brochure`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium"
                  data-testid="gc-brochure-download-btn"
                >
                  <FileText className="w-4 h-4" />
                  Download GC Brochure (PDF)
                </a>
              </div>
            </div>
          </div>
        </Section>

        {/* Hospital-Grade Security Suite */}
        <Section title="🛡️ Hospital-Grade Security Suite" icon={Shield} color="green">
          <div className="space-y-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <h4 className="text-xl font-bold text-emerald-800">Zero-Trust Security Architecture</h4>
                <span className="bg-green-500 text-white px-2 py-0.5 rounded text-xs font-bold">🟢 LIVE</span>
              </div>
              <p className="text-slate-700 mb-4">
                AskIsaac operates on a Zero-Trust Architecture, treating every request as a potential threat 
                until verified by MFA and RBAC. This ensures your facility infrastructure remains as secure 
                as your clinical data.
              </p>
              
              {/* Security Controls Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                <div className="bg-white rounded-lg p-4 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Lock className="w-5 h-5 text-emerald-600" />
                    <span className="font-semibold text-slate-800">Zero-Knowledge Auth</span>
                    <span className="bg-green-500 text-white px-1.5 py-0.5 rounded text-xs">LIVE</span>
                  </div>
                  <p className="text-sm text-slate-600">Bcrypt Salting (Cost Factor 12). No plain-text passwords exist in the system.</p>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Smartphone className="w-5 h-5 text-emerald-600" />
                    <span className="font-semibold text-slate-800">Multi-Factor Auth</span>
                    <span className="bg-green-500 text-white px-1.5 py-0.5 rounded text-xs">LIVE</span>
                  </div>
                  <p className="text-sm text-slate-600">TOTP-based MFA with Google Authenticator/Authy. Mandatory for Admin/Supplier roles.</p>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="w-5 h-5 text-emerald-600" />
                    <span className="font-semibold text-slate-800">Session Timeout</span>
                    <span className="bg-green-500 text-white px-1.5 py-0.5 rounded text-xs">LIVE</span>
                  </div>
                  <p className="text-sm text-slate-600">30-minute inactivity auto-logout. Protects terminals in high-traffic clinical areas.</p>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Database className="w-5 h-5 text-emerald-600" />
                    <span className="font-semibold text-slate-800">WORM Audit Logs</span>
                    <span className="bg-green-500 text-white px-1.5 py-0.5 rounded text-xs">LIVE</span>
                  </div>
                  <p className="text-sm text-slate-600">Immutable Write-Once-Read-Many logs with SHA-256 integrity hashing. 100% audit trail.</p>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertTriangle className="w-5 h-5 text-emerald-600" />
                    <span className="font-semibold text-slate-800">PHI Detection</span>
                    <span className="bg-green-500 text-white px-1.5 py-0.5 rounded text-xs">LIVE</span>
                  </div>
                  <p className="text-sm text-slate-600">Real-time scanning for SSN, MRN, and patient data patterns. Auto-blocks sensitive data.</p>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-emerald-200">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="w-5 h-5 text-emerald-600" />
                    <span className="font-semibold text-slate-800">RBAC Silos</span>
                    <span className="bg-green-500 text-white px-1.5 py-0.5 rounded text-xs">LIVE</span>
                  </div>
                  <p className="text-sm text-slate-600">Department-based access control: Supply Chain, Facilities, Biomed, SPD, and more.</p>
                </div>
              </div>

              {/* Technical Specs */}
              <div className="mt-6 bg-slate-900 rounded-lg p-4">
                <h5 className="text-white font-semibold mb-3">Technical Specifications</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-slate-400">Authentication</p>
                    <p className="text-emerald-400 font-mono">Bcrypt Salt-12</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Encryption</p>
                    <p className="text-emerald-400 font-mono">AES-256 / TLS 1.3</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Audit Logging</p>
                    <p className="text-emerald-400 font-mono">WORM + SHA-256</p>
                  </div>
                  <div>
                    <p className="text-slate-400">Data Residency</p>
                    <p className="text-emerald-400 font-mono">US-South (Texas)</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Pitch Script */}
            <ScriptCard
              title="Security Pitch (IT/CISO)"
              script="We understand your security team needs more than marketing promises. AskIsaac operates on a Zero-Trust Architecture with TOTP-based MFA, immutable WORM audit logs, and real-time PHI detection. Passwords are encrypted using Bcrypt with Salt Factor 12 — even our SuperAdmins can't see them. Sessions auto-terminate after 30 minutes of inactivity. Every action is logged with SHA-256 integrity hashing for Joint Commission audits. You can view our full security whitepaper at sterilemate.com/security-whitepaper."
              tips="Offer to schedule a dedicated security review with their IT team."
            />

            {/* Compliance Disclosure */}
            <div className="bg-slate-100 rounded-lg p-4 border border-slate-300">
              <h5 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                HIPAA Administrative Safeguards Disclosure
              </h5>
              <p className="text-sm text-slate-600 italic">
                "AskIsaac utilizes a Zero-Knowledge authentication architecture. User credentials are encrypted 
                using salted Bcrypt hashing (Cost Factor 12), ensuring that plain-text passwords are never stored, 
                transmitted, or viewable by AskIsaac administrative personnel. All communications are logged using 
                WORM (Write Once, Read Many) methodology with SHA-256 integrity verification."
              </p>
              <div className="mt-3 flex items-center gap-2">
                <a 
                  href="/security-whitepaper" 
                  target="_blank"
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" />
                  View Full Security Whitepaper
                </a>
              </div>
            </div>
          </div>
        </Section>

        {/* Fortress Compliance Engine */}
        <Section title="🏰 The Fortress Compliance Engine" icon={Shield} color="red">
          <div className="space-y-6">
            <div className="bg-red-50 border border-red-200 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <h4 className="text-xl font-bold text-red-800">Real-Time Compliance Gateway</h4>
                <span className="bg-blue-500 text-white px-2 py-0.5 rounded text-xs font-bold">Q1 2026</span>
              </div>
              <p className="text-slate-700 mb-4">
                AskIsaac is building a <strong>"Trust Gateway"</strong> that will serve both Government and Private Sector requirements. 
                Every vendor in the system will pass through continuous compliance verification.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Government Sector */}
                <div className="bg-white rounded-lg p-4 border border-red-200">
                  <h5 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    Government Sector Compliance
                  </h5>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">Q1</span>
                      <strong>OIG Exclusion List</strong> — Real-time screening
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">Q1</span>
                      <strong>SAM.gov Registration</strong> — Active status verification
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">Q1</span>
                      <strong>OFAC Sanctions</strong> — Debarment monitoring
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs">Q2</span>
                      <strong>Federal Certifications</strong> — 8(a), HUBZone, SDVOSB
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs">Q3</span>
                      <strong>FedRAMP Readiness</strong> — Security framework aligned
                    </li>
                  </ul>
                </div>
                
                {/* Private Sector */}
                <div className="bg-white rounded-lg p-4 border border-red-200">
                  <h5 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-purple-600" />
                    Private Sector Compliance
                  </h5>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs">LIVE</span>
                      <strong>Insurance Verification</strong> — COI uploads & tracking
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs">LIVE</span>
                      <strong>License Validation</strong> — Document uploads
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">Q1</span>
                      <strong>HIPAA Alignment</strong> — BAA documentation
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs">Q2</span>
                      <strong>Financial Stability</strong> — D&B monitoring
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs">LIVE</span>
                      <strong>Performance Scorecards</strong> — KPI tracking
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-6 bg-red-100 rounded-lg p-4 border border-red-300">
                <div className="flex items-center gap-2 mb-2">
                  <h5 className="font-bold text-red-800">🚫 System Lock Protocol</h5>
                  <span className="bg-blue-500 text-white px-2 py-0.5 rounded text-xs font-bold">Q1 2026</span>
                </div>
                <p className="text-slate-700 text-sm">
                  When implemented, if any vendor&apos;s status becomes <strong>"Excluded"</strong> or <strong>"Expired"</strong>, 
                  the Fortress Gate will trigger an automatic <strong>System Lock</strong> — preventing that vendor from 
                  appearing in active procurement views until remediation is complete.
                </p>
              </div>
            </div>
            
            <ScriptCard 
              title="Compliance Pitch (Government)"
              script="Your federal funding depends on vendor compliance. One excluded vendor in your supply chain can trigger False Claims Act liability. We're building our Fortress Engine to screen every vendor against OIG, SAM.gov, and OFAC in real-time — launching Q1 2026. When a vendor gets excluded, they'll disappear from your procurement options automatically. That's institutional protection by design."
              tips="Be transparent about timeline. Lead with the vision, confirm the roadmap."
            />
            
            <ScriptCard 
              title="Compliance Pitch (Private Sector)"
              script="Joint Commission doesn't care about promises — they care about documentation. Today, our platform already tracks COIs, licenses, and W9s. By Q1, we're adding automated verification and one-click audit exports. When the auditor asks for proof, you won't scramble — you'll click 'Export' and hand them a timestamped compliance report."
              tips="Start with what's live NOW, then paint the near-term vision."
            />
          </div>
        </Section>

        {/* VHI Ranking Algorithm */}
        <Section title="📊 The VHI Ranking Algorithm" icon={BarChart3} color="purple">
          <div className="space-y-6">
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <h4 className="text-xl font-bold text-purple-800">Vendor Health Index — Strategic Procurement Intelligence</h4>
                <span className="bg-blue-500 text-white px-2 py-0.5 rounded text-xs font-bold">Q1 2026</span>
              </div>
              <p className="text-slate-700 mb-4">
                AskIsaac is developing a weighted algorithm to <strong>mathematically prioritize the RIGHT vendors</strong> based on 
                institutional goals — not just lowest price.
              </p>
              
              <div className="grid md:grid-cols-3 gap-4 mb-6">
                <div className="bg-white rounded-lg p-4 border border-purple-200 text-center">
                  <div className="text-4xl font-bold text-purple-600 mb-2">40%</div>
                  <h5 className="font-semibold text-slate-800 mb-1">Federal/State Compliance</h5>
                  <p className="text-sm text-slate-600">OIG, SAM.gov, State Licenses, Insurance Currency</p>
                  <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs mt-2 inline-block">Q1 2026</span>
                </div>
                <div className="bg-white rounded-lg p-4 border border-purple-200 text-center">
                  <div className="text-4xl font-bold text-emerald-600 mb-2">40%</div>
                  <h5 className="font-semibold text-slate-800 mb-1">Documented Performance</h5>
                  <p className="text-sm text-slate-600">On-Time Delivery, Quality Scores, Response Time KPIs</p>
                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs mt-2 inline-block">LIVE</span>
                </div>
                <div className="bg-white rounded-lg p-4 border border-purple-200 text-center">
                  <div className="text-4xl font-bold text-amber-600 mb-2">20%</div>
                  <h5 className="font-semibold text-slate-800 mb-1">Social Impact</h5>
                  <p className="text-sm text-slate-600">Minority-Owned, Veteran-Owned, Local/Regional Status</p>
                  <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs mt-2 inline-block">Q1 2026</span>
                </div>
              </div>
              
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <h5 className="font-bold text-amber-800 mb-2">🇺🇸 America First Procurement Alignment</h5>
                <p className="text-slate-700 text-sm">
                  The VHI algorithm will mathematically prioritize <strong>high-value local creators</strong> over generic 
                  conglomerate vendors. This aligns with federal mandates, state spending goals, and community investment initiatives.
                </p>
              </div>
            </div>
            
            <ScriptCard 
              title="VHI Pitch"
              script="Most platforms sort by lowest price. We're building something smarter. Our VHI algorithm — launching Q1 — will weigh compliance, performance, AND social impact. Today we already track performance KPIs. Soon, when you procure through AskIsaac, you'll automatically meet your minority-spend targets, your local-first mandates, and your performance requirements — the math will do the mission alignment for you."
              tips="Performance tracking is live NOW. The weighted algorithm is the Q1 enhancement."
            />
          </div>
        </Section>

        {/* One-Click Audit Export */}
        <Section title="📋 One-Click Audit Export" icon={FileText} color="green">
          <div className="space-y-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <h4 className="text-xl font-bold text-emerald-800">Secure Audit Export — Accreditation Ready</h4>
                <span className="bg-blue-500 text-white px-2 py-0.5 rounded text-xs font-bold">Q1 2026</span>
              </div>
              <p className="text-slate-700 mb-4">
                The #1 complaint from healthcare compliance officers: <strong>"I spent 40 hours gathering vendor documents for our Joint Commission audit."</strong>
              </p>
              <p className="text-slate-700 mb-4">
                AskIsaac is building <strong>One-Click Audit Export</strong> to eliminate 90% of that labor.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-4 border border-emerald-200">
                  <h5 className="font-bold text-slate-800 mb-3">What&apos;s Included in Export:</h5>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs">LIVE</span>
                      Certificates of Insurance (COI) — Current & Historical
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs">LIVE</span>
                      W9 Tax Documentation
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs">LIVE</span>
                      License Verifications & Expiry Tracking
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs">LIVE</span>
                      Performance Scorecards by Vendor
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">Q1</span>
                      Compliance Check Timestamps
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs">LIVE</span>
                      Transaction Audit Trail
                    </li>
                  </ul>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-emerald-200">
                  <h5 className="font-bold text-slate-800 mb-3">Export Capabilities:</h5>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs">LIVE</span>
                      <strong>Single Vendor</strong> — View documents per contractor
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">Q1</span>
                      <strong>Date Range</strong> — All activity for audit period
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">Q1</span>
                      <strong>Department</strong> — Filtered by cost center
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-xs">Q1</span>
                      <strong>Full Compliance Report</strong> — Encrypted PDF bundle
                    </li>
                  </ul>
                  <div className="mt-4 p-3 bg-emerald-100 rounded-lg">
                    <p className="text-sm text-emerald-800 font-medium">
                      🔒 Document storage is live. Automated export bundling coming Q1 2026.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <ScriptCard 
              title="Audit Export Pitch"
              script="Today, all your vendor documents — COIs, W9s, licenses — are already stored in AskIsaac. You can view them anytime. By Q1, we're launching One-Click Export: select your date range, click a button, and download an encrypted PDF with everything the auditor needs. What takes 40 hours today will take 40 seconds. The foundation is built — the automation is next."
              tips="Documents are stored NOW. The one-click bundled export is the Q1 feature."
            />
          </div>
        </Section>

        {/* Board-Ready Executive Summary */}
        <Section title="📄 Board-Ready Executive Summary Generator" icon={FileText} color="orange">
          <div className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <h4 className="text-xl font-bold text-amber-800">The Closer — Automated Impact Report</h4>
                <span className="bg-green-500 text-white px-2 py-0.5 rounded text-xs font-bold">LIVE</span>
              </div>
              <p className="text-slate-700 mb-4">
                When your demo ends, you don&apos;t say "we&apos;ll follow up." You hit a button and say: 
                <strong> "I&apos;ve just emailed you a Board-Ready Executive Summary that outlines exactly how much money 
                we&apos;re saving you and how we&apos;re protecting your hospital&apos;s reputation. This is what ownership looks like."</strong>
              </p>
              
              <div className="bg-white rounded-xl p-6 border border-amber-300 shadow-sm">
                <div className="border-b border-slate-200 pb-4 mb-4">
                  <p className="text-sm text-slate-500">AUTOMATED REPORT TEMPLATE</p>
                  <h5 className="text-xl font-bold text-slate-800">Procurement Sovereignty Summary</h5>
                  <p className="text-sm text-slate-600">Strategic Impact Assessment for Hospital Leadership</p>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-500">
                    <h6 className="font-bold text-red-800 mb-2">I. Risk Neutralization</h6>
                    <p className="text-sm text-slate-700">
                      The AskIsaac engine has verified <strong>100%</strong> of the current vendor pool against Federal OIG/SAM databases. 
                      The "Fortress" gate provides 24/7 monitoring, reducing institutional liability by <strong>[X]%</strong>.
                    </p>
                    <p className="text-xs text-red-600 mt-2">* Metrics calculated from user inputs; live API verification coming Q1 2026</p>
                  </div>
                  
                  <div className="bg-emerald-50 rounded-lg p-4 border-l-4 border-emerald-500">
                    <h6 className="font-bold text-emerald-800 mb-2">II. Operational Efficiency (The ROI)</h6>
                    <p className="text-sm text-slate-700">
                      By automating manual credentialing and performance tracking, AskIsaac reclaims approximately 
                      <strong> [X] hours per month</strong> of administrative labor, representing a cost-savings of 
                      <strong> $[X] annually</strong>.
                    </p>
                  </div>
                  
                  <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-500">
                    <h6 className="font-bold text-blue-800 mb-2">III. Economic Sovereignty & Inclusion</h6>
                    <p className="text-sm text-slate-700">
                      Through our weighted VHI algorithm, your institution is now statistically prioritized to support 
                      <strong> American-owned and Minority-led firms</strong>, aligning procurement with "America First" 
                      standards without sacrificing performance.
                    </p>
                    <p className="text-xs text-blue-600 mt-2">* VHI algorithm launching Q1 2026</p>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-slate-200 text-center">
                  <p className="text-sm text-slate-500">Generated by AskIsaac Governance Platform</p>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-green-100 rounded-lg border border-green-300">
                <p className="text-sm text-green-800">
                  <strong>✅ This feature is LIVE.</strong> Access it from Admin Dashboard → Executive Summary. 
                  Enter prospect details, generate the report, and email it directly to leadership.
                </p>
              </div>
            </div>
            
            <ScriptCard 
              title="The Executive Summary Close"
              script="Before I leave, I want to give you something tangible. [Click button] I've just generated a Board-Ready Executive Summary — it shows exactly how AskIsaac reduces your compliance risk, how many hours we'll save your team, and how we align with your diversity spending mandates. This isn't a brochure. This is the document your Supply Chain Chief takes to the Board to justify this partnership. Check your inbox — it's already there."
              tips="This is THE closer. The generator is LIVE — use it at the end of every demo."
            />
          </div>
        </Section>

        {/* ERP Integration Story */}
        <Section title="🔗 ERP & System Integration" icon={Layers} color="blue">
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <h4 className="text-xl font-bold text-blue-800">Enterprise Integration Architecture</h4>
                <span className="bg-purple-500 text-white px-2 py-0.5 rounded text-xs font-bold">ROADMAP</span>
              </div>
              <p className="text-slate-700 mb-4">
                AskIsaac is designed to <strong>complement, not replace</strong> your existing systems. 
                Our architecture supports ERP integration — implementations available on customer request.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <h5 className="font-bold text-slate-800 mb-3">Integration-Ready Architecture:</h5>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs">ROADMAP</span>
                      <strong>Workday</strong> — Financial & procurement sync
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs">ROADMAP</span>
                      <strong>Oracle Cloud</strong> — Supply chain management
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs">ROADMAP</span>
                      <strong>SAP</strong> — Materials management (MM)
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs">ROADMAP</span>
                      <strong>Infor</strong> — Healthcare-specific modules
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded text-xs">ROADMAP</span>
                      <strong>MEDITECH</strong> — Materials management
                    </li>
                  </ul>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-blue-200">
                  <h5 className="font-bold text-slate-800 mb-3">Current Capabilities:</h5>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs">LIVE</span>
                      RESTful API — Custom integrations available
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs">LIVE</span>
                      QuickBooks Online — Accounting sync
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs">LIVE</span>
                      Email Integration — Microsoft Graph API
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs">LIVE</span>
                      Telegram — Real-time notifications
                    </li>
                    <li className="flex items-center gap-2 text-sm text-slate-600">
                      <span className="bg-green-100 text-green-700 px-1.5 py-0.5 rounded text-xs">LIVE</span>
                      Webhook Support — Event-driven triggers
                    </li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-4 p-3 bg-blue-100 rounded-lg border border-blue-300">
                <p className="text-sm text-blue-800">
                  <strong>Integration Note:</strong> Enterprise ERP integrations (Workday, Oracle, SAP) are built on-demand 
                  based on customer requirements. Our API-first architecture makes this straightforward.
                </p>
              </div>
            </div>
            
            <ScriptCard 
              title="ERP Integration Pitch"
              script="I know what you're thinking — 'another system to manage.' Here's the reality: AskIsaac isn't replacing your ERP. We work alongside it. We already integrate with QuickBooks for accounting and Microsoft for email. For enterprise ERP connections like Workday or Oracle, we build those integrations based on customer requirements — our API-first architecture makes it straightforward. You tell us what you need; we make it happen."
              tips="Be honest: QuickBooks and email are live. Enterprise ERP is available but built on-demand."
            />
          </div>
        </Section>

        {/* Platform Evolution Roadmap */}
        <Section title="🚀 Platform Evolution Roadmap" icon={TrendingUp} color="purple">
          <div className="space-y-6">
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-6">
              <h4 className="text-xl font-bold text-purple-800 mb-4">Where AskIsaac Is Going</h4>
              <p className="text-slate-700 mb-6">
                AskIsaac is built on <strong>open architecture principles</strong> — designed to evolve with your needs 
                while maintaining full compliance with government and private sector standards.
              </p>
              
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-4 border border-green-300">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="bg-green-500 text-white px-2 py-1 rounded text-xs font-bold">✅ LIVE NOW</span>
                    <h5 className="font-bold text-slate-800">Phase 1: Core Operations Platform</h5>
                  </div>
                  <p className="text-sm text-slate-600">
                    Multi-portal architecture, maintenance pipeline (8-stage), QuickBid competitive bidding, 
                    GPS fleet tracking, project management, contractor credentialing, Telegram integration, 
                    email campaigns, lead capture, executive summary generator.
                  </p>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-blue-300">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="bg-blue-500 text-white px-2 py-1 rounded text-xs font-bold">🔵 Q1 2026</span>
                    <h5 className="font-bold text-slate-800">Phase 2: Governance & Compliance Suite</h5>
                  </div>
                  <p className="text-sm text-slate-600">
                    Fortress Compliance Engine (OIG/SAM/OFAC API integration), VHI Ranking Algorithm, 
                    One-Click Audit Export (encrypted PDF bundle), automated compliance timestamps.
                  </p>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-purple-300">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="bg-purple-500 text-white px-2 py-1 rounded text-xs font-bold">🟣 Q2 2026</span>
                    <h5 className="font-bold text-slate-800">Phase 3: AI-Powered Procurement Intelligence</h5>
                  </div>
                  <p className="text-sm text-slate-600">
                    Predictive supply chain alerts, automated reorder recommendations, price trend analysis, 
                    vendor risk scoring with ML, natural language procurement requests.
                  </p>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-amber-300">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="bg-amber-500 text-white px-2 py-1 rounded text-xs font-bold">🟠 Q3 2026</span>
                    <h5 className="font-bold text-slate-800">Phase 4: Multi-Industry Expansion</h5>
                  </div>
                  <p className="text-sm text-slate-600">
                    Manufacturing sector module, government/municipal procurement, education sector compliance, 
                    hospitality operations, property management integration.
                  </p>
                </div>
              </div>
              
              <div className="mt-6 bg-purple-100 rounded-lg p-4 border border-purple-300">
                <h5 className="font-bold text-purple-800 mb-2">🔓 Open Architecture Philosophy</h5>
                <p className="text-sm text-slate-700">
                  AskIsaac maintains <strong>API transparency</strong> and <strong>data portability</strong> as core principles. 
                  Your data is yours. Our integrations are documented. Security certifications (SOC 2, FedRAMP) are on the roadmap 
                  as we scale with enterprise customers.
                </p>
              </div>
            </div>
          </div>
        </Section>

        {/* Interactive ROI Calculator */}
        <Section title="💰 ROI Calculator — The Business Case" icon={DollarSign} color="green">
          <div className="space-y-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h4 className="text-xl font-bold text-emerald-800 mb-4">Quantified Value Proposition</h4>
              <p className="text-slate-700 mb-4">
                Use these benchmarks when building the business case for hospital leadership.
              </p>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-4 border border-emerald-200">
                  <h5 className="font-bold text-slate-800 mb-3">Time Savings (Annual)</h5>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Vendor Credentialing</span>
                      <span className="font-semibold text-emerald-600">240 hrs saved</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Audit Preparation</span>
                      <span className="font-semibold text-emerald-600">160 hrs saved</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Quote Comparison</span>
                      <span className="font-semibold text-emerald-600">200 hrs saved</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Emergency Sourcing</span>
                      <span className="font-semibold text-emerald-600">120 hrs saved</span>
                    </div>
                    <div className="flex justify-between items-center border-t pt-2">
                      <span className="font-bold text-slate-800">Total Annual Hours</span>
                      <span className="font-bold text-emerald-600">720 hrs</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-emerald-200">
                  <h5 className="font-bold text-slate-800 mb-3">Cost Impact (Annual)</h5>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Labor Savings (720 hrs × $45/hr)</span>
                      <span className="font-semibold text-emerald-600">$32,400</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Competitive Bid Savings (avg 12%)</span>
                      <span className="font-semibold text-emerald-600">$50,000+</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Emergency Premium Reduction</span>
                      <span className="font-semibold text-emerald-600">$25,000+</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-slate-600">Risk Mitigation Value</span>
                      <span className="font-semibold text-emerald-600">$100,000+</span>
                    </div>
                    <div className="flex justify-between items-center border-t pt-2">
                      <span className="font-bold text-slate-800">Total Annual Value</span>
                      <span className="font-bold text-emerald-600">$207,400+</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 bg-amber-50 rounded-lg p-4 border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>💡 Sales Tip:</strong> These are conservative estimates for a mid-sized facility. 
                  For large health systems, multiply by 3-5x. Always customize based on their stated pain points.
                </p>
              </div>
            </div>
            
            <ScriptCard 
              title="ROI Conversation"
              script="Let me put some numbers to this. Based on what you've told me about your current process, you're spending roughly 60 hours a month on vendor management tasks that AskIsaac automates. That's 720 hours a year — at $45/hour loaded cost, that's $32,000 in labor alone. Add the 12% average savings from competitive bidding, and we're looking at over $200,000 in annual value. The platform pays for itself in the first quarter."
              tips="Always tie back to THEIR numbers. Ask about their current hours spent, then calculate live."
            />
          </div>
        </Section>

        {/* ============================================ */}
        {/* JANITORIAL & EVS CAPABILITIES SECTION */}
        {/* ============================================ */}
        
        <Section title="🧹 Janitorial & EVS Capabilities" icon={Sparkles} color="green">
          <div className="space-y-8">
            {/* Hero Banner */}
            <div className="relative overflow-hidden rounded-2xl">
              <img 
                src="https://sterilemate.com/wp-content/uploads/2025/11/Autonomous-Floor-Care.webp" 
                alt="Janitorial & EVS Solutions" 
                className="w-full h-72 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/95 via-teal-800/80 to-transparent flex items-center">
                <div className="p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center border border-white/30">
                      <Sparkles className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">Janitorial & EVS Division</h3>
                      <p className="text-emerald-200">5+ Years Serving the Texas Medical Center</p>
                    </div>
                  </div>
                  <div className="bg-emerald-500/30 backdrop-blur rounded-xl p-4 border border-emerald-400/50 inline-block mb-4">
                    <p className="text-white font-bold text-lg flex items-center gap-2">
                      <Award className="w-5 h-5 text-emerald-300" />
                      Hospital-Grade Solutions | Authorized Distributor for Leading Brands
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <a 
                      href="https://sterilemate.com/janitorial-environmental-service-supplies/" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors font-medium"
                    >
                      <ExternalLink className="w-4 h-4" />
                      EVS Product Catalog
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Opening Hook */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Play className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-2">🎬 Opening Hook for EVS Demo</h4>
                  <p className="text-white/90 text-lg italic">
                    &quot;You&apos;re not just cleaning floors—you&apos;re preventing HAIs, protecting patients, and maintaining Joint Commission standards. 
                    We&apos;ve been supplying hospital-grade janitorial solutions to the Texas Medical Center for over 5 years. 
                    From autonomous floor scrubbers to sustainable microfiber systems, we have the complete toolkit for healthcare EVS.&quot;
                  </p>
                </div>
              </div>
            </div>

            {/* Authorized Distributor Badges */}
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <h4 className="font-bold text-slate-800 mb-4 text-center flex items-center justify-center gap-2">
                <BadgeCheck className="w-5 h-5 text-emerald-600" />
                Authorized Distributor for Industry-Leading Brands
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {[
                  { name: "Filmop USA", specialty: "Workstations & Microfiber", url: "https://www.filmopusa.com" },
                  { name: "MultiClean", specialty: "Cleaning Chemicals", url: "https://www.multi-clean.com" },
                  { name: "Minuteman", specialty: "Floor Equipment", url: "https://www.minutemanintl.com" },
                  { name: "Microfiber USA", specialty: "Microfiber Products", url: "https://www.microfiberwholesale.com" },
                  { name: "ABCO Products", specialty: "Mops & Brooms", url: "https://abcoproducts.com" }
                ].map((brand, i) => (
                  <a 
                    key={i}
                    href={brand.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white rounded-lg p-4 text-center border border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all"
                  >
                    <p className="font-bold text-slate-800">{brand.name}</p>
                    <p className="text-xs text-emerald-600">{brand.specialty}</p>
                  </a>
                ))}
              </div>
            </div>

            {/* Product Categories */}
            <div>
              <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-emerald-600" />
                Product Categories
              </h4>
              <div className="grid md:grid-cols-3 gap-6">
                {/* Janitorial Disposables */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-emerald-600 p-4 text-white">
                    <h5 className="font-bold text-lg">Janitorial Disposables</h5>
                    <p className="text-emerald-100 text-sm">Daily consumables for healthcare EVS</p>
                  </div>
                  <div className="p-4 space-y-2">
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-emerald-500" />
                      Trash Liners (All Sizes)
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-emerald-500" />
                      Gloves (Nitrile, Latex, Vinyl)
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-emerald-500" />
                      Paper Products (Towels, Tissue)
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-emerald-500" />
                      Disinfectant Wipes
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-emerald-500" />
                      Disposable Mop Heads
                    </p>
                  </div>
                </div>

                {/* Microfiber Products */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-blue-600 p-4 text-white">
                    <h5 className="font-bold text-lg">Microfiber Systems</h5>
                    <p className="text-blue-100 text-sm">Disposable & reusable options</p>
                  </div>
                  <div className="p-4 space-y-2">
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-blue-500" />
                      Flat Mop Pads (Multiple Colors)
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-blue-500" />
                      Microfiber Cloths
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-blue-500" />
                      Dust Mops
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-blue-500" />
                      String Mops
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-blue-500" />
                      Color-Coded Systems
                    </p>
                  </div>
                </div>

                {/* Hardware Solutions */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <div className="bg-violet-600 p-4 text-white">
                    <h5 className="font-bold text-lg">Hardware Solutions</h5>
                    <p className="text-violet-100 text-sm">Hospital-grade equipment</p>
                  </div>
                  <div className="p-4 space-y-2">
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-violet-500" />
                      Walk-Behind Scrubbers
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-violet-500" />
                      Autonomous Floor Care
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-violet-500" />
                      Critical Filter Vacuums
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-violet-500" />
                      EVS Workstations (Filmop Alpha)
                    </p>
                    <p className="text-sm text-slate-600 flex items-center gap-2">
                      <CircleCheck className="w-4 h-4 text-violet-500" />
                      Janitorial Carts
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Highlights with Images */}
            <div>
              <h4 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-amber-500" />
                Product Highlights
              </h4>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Autonomous Floor Care */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow">
                  <img 
                    src="https://sterilemate.com/wp-content/uploads/2025/11/Autonomous-Floor-Care.webp" 
                    alt="Autonomous Floor Care"
                    className="w-full h-40 object-cover"
                  />
                  <div className="p-4">
                    <h5 className="font-bold text-slate-800">Autonomous Floor Care</h5>
                    <p className="text-sm text-slate-600 mt-1">Self-navigating scrubbers for 24/7 operation</p>
                    <p className="text-xs text-emerald-600 mt-2 font-medium">By Minuteman</p>
                  </div>
                </div>

                {/* Filmop Workstation */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow">
                  <img 
                    src="https://www.filmopusa.com/images/product-page-images/workstation-overview-top-950.gif" 
                    alt="Filmop Alpha Workstation"
                    className="w-full h-40 object-cover"
                  />
                  <div className="p-4">
                    <h5 className="font-bold text-slate-800">Alpha Workstations</h5>
                    <p className="text-sm text-slate-600 mt-1">Customizable EVS carts with security features</p>
                    <p className="text-xs text-emerald-600 mt-2 font-medium">By Filmop USA</p>
                  </div>
                </div>

                {/* Critical Filter Vacuum */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow">
                  <img 
                    src="https://sterilemate.com/wp-content/uploads/2025/11/Hospital-Vacuum-1.webp" 
                    alt="Critical Filter Vacuum"
                    className="w-full h-40 object-cover"
                  />
                  <div className="p-4">
                    <h5 className="font-bold text-slate-800">Critical Filter Vacuums</h5>
                    <p className="text-sm text-slate-600 mt-1">HEPA filtration for healthcare environments</p>
                    <p className="text-xs text-emerald-600 mt-2 font-medium">Hospital Grade</p>
                  </div>
                </div>

                {/* Walk Behind Scrubber */}
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow">
                  <img 
                    src="https://sterilemate.com/wp-content/uploads/2025/11/EZ-Mop-360-Microscrubber-1.webp" 
                    alt="Walk Behind Floor Scrubber"
                    className="w-full h-40 object-cover"
                  />
                  <div className="p-4">
                    <h5 className="font-bold text-slate-800">Floor Scrubbers</h5>
                    <p className="text-sm text-slate-600 mt-1">Walk-behind and ride-on options</p>
                    <p className="text-xs text-emerald-600 mt-2 font-medium">All Sizes Available</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Filmop Spotlight */}
            <div className="bg-gradient-to-r from-blue-900 to-indigo-900 rounded-xl p-6 text-white">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <img 
                  src="https://www.filmopusa.com/images/product-page-images/workstation-basic-250x150.gif" 
                  alt="Filmop Workstation"
                  className="w-48 h-32 object-contain bg-white rounded-lg p-2"
                />
                <div>
                  <h4 className="text-xl font-bold mb-2">Filmop USA Alpha Series</h4>
                  <p className="text-blue-200 mb-4">
                    The Alpha workstation series offers fully customizable EVS carts with lockable compartments, 
                    integrated charging systems, and ergonomic design. Perfect for healthcare environments 
                    requiring security and efficiency.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="bg-blue-500/30 px-3 py-1 rounded-full text-sm">Customizable Configurations</span>
                    <span className="bg-blue-500/30 px-3 py-1 rounded-full text-sm">Security Features</span>
                    <span className="bg-blue-500/30 px-3 py-1 rounded-full text-sm">Ergonomic Design</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Chemical Management */}
            <div className="bg-amber-50 rounded-xl p-6 border border-amber-200">
              <h4 className="font-bold text-amber-800 mb-4 flex items-center gap-2">
                <FlaskConical className="w-5 h-5" />
                Chemical Management System (By MultiClean)
              </h4>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 border border-amber-200">
                  <img 
                    src="https://sterilemate.com/wp-content/uploads/2025/11/Bio-Power-1.webp" 
                    alt="Commercial Cleaning Chemicals"
                    className="w-full h-24 object-contain mb-2"
                  />
                  <p className="font-semibold text-slate-800 text-sm">Commercial Cleaners</p>
                  <p className="text-xs text-slate-500">Full product line</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-amber-200">
                  <img 
                    src="https://sterilemate.com/wp-content/uploads/2025/11/128-E-Fecticide-2L-1.webp" 
                    alt="Dilution Control"
                    className="w-full h-24 object-contain mb-2"
                  />
                  <p className="font-semibold text-slate-800 text-sm">Dilution Control</p>
                  <p className="text-xs text-slate-500">Concentrated systems</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-amber-200">
                  <img 
                    src="https://sterilemate.com/wp-content/uploads/2025/11/950-Bowl-Cleaner-1.webp" 
                    alt="Restroom Care"
                    className="w-full h-24 object-contain mb-2"
                  />
                  <p className="font-semibold text-slate-800 text-sm">Restroom Care</p>
                  <p className="text-xs text-slate-500">Specialized formulas</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-amber-200">
                  <img 
                    src="https://sterilemate.com/wp-content/uploads/2025/11/atp-device.webp" 
                    alt="ATP Testing"
                    className="w-full h-24 object-contain mb-2"
                  />
                  <p className="font-semibold text-slate-800 text-sm">ATP Testing</p>
                  <p className="text-xs text-slate-500">Cleaning verification</p>
                </div>
              </div>
            </div>

            {/* ABCO Green Products */}
            <div className="bg-emerald-50 rounded-xl p-6 border border-emerald-200">
              <h4 className="font-bold text-emerald-800 mb-4 flex items-center gap-2">
                <Award className="w-5 h-5" />
                Green Seal Certified Products (By ABCO)
              </h4>
              <p className="text-slate-700 mb-4">
                ABCO Products is the first and only manufacturer with Green Seal GS-20 certified mop heads and dust mops, 
                plus UL Environmental Claims Validation for their Natura line.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-4 border border-emerald-200 text-center">
                  <p className="text-2xl mb-2">🌿</p>
                  <p className="font-bold text-emerald-700">Green Seal GS-20</p>
                  <p className="text-xs text-slate-500">First certified mop heads</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-emerald-200 text-center">
                  <p className="text-2xl mb-2">🏆</p>
                  <p className="font-bold text-emerald-700">ISSA 2023 Winner</p>
                  <p className="text-xs text-slate-500">Innovation Award</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-emerald-200 text-center">
                  <p className="text-2xl mb-2">♻️</p>
                  <p className="font-bold text-emerald-700">UL Validated</p>
                  <p className="text-xs text-slate-500">Environmental claims</p>
                </div>
              </div>
            </div>

            {/* Sales Scripts */}
            <div className="space-y-4">
              <h4 className="text-lg font-bold text-slate-800">📝 Sales Scripts for EVS</h4>
              
              <ScriptCard 
                title="Opening for EVS Directors"
                script="I know your team is under pressure to maintain infection control standards while managing tighter budgets. We've been supplying hospital-grade EVS solutions to the Texas Medical Center for over 5 years. What if I told you we could help you standardize on proven systems—from Filmop workstations to ABCO's Green Seal certified mops—while potentially reducing your per-unit costs through our distributor pricing?"
                tips="Lead with their pain points: infection control + budget pressure. Position as partner, not vendor."
              />
              
              <ScriptCard 
                title="Autonomous Floor Care Pitch"
                script="Your night crew is stretched thin. What if you could have autonomous scrubbers working the hallways at 2 AM while your team focuses on patient rooms and high-touch surfaces? These units map your facility, clean on schedule, and generate compliance reports. It's like having an extra team member who never calls in sick."
                tips="Emphasize labor efficiency and compliance documentation."
              />

              <ScriptCard 
                title="Microfiber System Conversion"
                script="How much are you spending annually on string mops? The math usually surprises people. When we help facilities convert to microfiber—flat mop systems with color-coding for infection control—they typically see 25-40% reduction in water and chemical usage, plus better outcomes on ATP testing. And with reusable microfiber, your per-clean cost drops dramatically."
                tips="Always offer to do the ROI calculation with their current spend."
              />
            </div>

            {/* Objection Handlers */}
            <div className="space-y-3">
              <h4 className="font-semibold text-slate-800">🛡️ Objection Handlers</h4>
              <ObjectionHandler 
                objection="We already have a janitorial supplier"
                response="That's great—we're not looking to replace everything. Most of our hospital clients started by adding one product line: maybe the Filmop workstations for security, or autonomous scrubbers to solve a staffing gap. We complement existing relationships. Can I show you one thing that might solve a specific pain point?"
              />
              <ObjectionHandler 
                objection="Your prices are higher than our current vendor"
                response="Let's look at total cost of ownership. Yes, a cheap mop costs less—but if it lasts half as long and doesn't clean as well, you're buying more and using more chemicals. Our hospital-grade products are built for 500+ uses versus 50. Want me to run the numbers with your current usage?"
              />
              <ObjectionHandler 
                objection="We don't have budget for new equipment"
                response="I hear that a lot. Two options: First, we can start with consumables—microfiber, chemicals—that replace existing spend. Second, for equipment like autonomous scrubbers, there are often capital equipment budgets or even lease options that don't hit your operating budget. What's your fiscal year timing?"
              />
            </div>

            {/* Contact CTA */}
            <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-xl p-6 text-white text-center">
              <h4 className="text-xl font-bold mb-2">Ready to Upgrade Your EVS Program?</h4>
              <p className="text-emerald-100 mb-4">Contact us for product demos, samples, or a facility assessment.</p>
              <div className="flex justify-center gap-4">
                <a 
                  href="tel:8009883892"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-emerald-700 rounded-lg hover:bg-emerald-50 transition-colors font-medium"
                >
                  <Phone className="w-5 h-5" />
                  800.988.3892
                </a>
                <a 
                  href="https://sterilemate.com/janitorial-environmental-service-supplies/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-700 text-white border border-emerald-500 rounded-lg hover:bg-emerald-800 transition-colors font-medium"
                >
                  <ExternalLink className="w-5 h-5" />
                  View Full Catalog
                </a>
              </div>
            </div>
          </div>
        </Section>

        {/* Quick Reference */}
        <div className="mt-8 bg-gradient-to-r from-slate-900 to-blue-900 rounded-xl p-6 text-white">
          <h3 className="text-lg font-bold mb-4">Quick Reference</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm">
            <div>
              <h4 className="font-semibold text-blue-300 mb-2">Key Contacts</h4>
              <p>George Broughton III</p>
              <p>Toll Free: 800.988.3892</p>
              <p>Direct: 281.456.3458</p>
            </div>
            <div>
              <h4 className="font-semibold text-blue-300 mb-2">Demo URLs</h4>
              <p>sterilemate.com/demo</p>
              <p>sterilemate.com/hospital-presentation</p>
              <p>sterilemate.com/investor</p>
            </div>
            <div>
              <h4 className="font-semibold text-blue-300 mb-2">Current Beta</h4>
              <p>Major Public Health System</p>
              <p>Texas Medical Center</p>
              <p>Houston, TX</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
